/**
 * O escritório 2D — a planta da empresa: geometria, salas, mobília e cadência.
 *
 * É lógica pura, não uma forma de fio: por isso vive à parte de `entities.ts`.
 * Uma ÚNICA fonte de verdade partilhada — o servidor e o cliente NUNCA discordam
 * de onde está a parede ou o sofá, porque a planta é definida uma só vez, aqui:
 *   - o SERVIDOR importa só `clampToSpace` (nunca deixa ninguém sair do chão);
 *   - o CLIENTE importa a planta inteira (salas, portas, mobília) para desenhar
 *     o mundo e colidir com ele.
 *
 * O mundo é uma verdadeira planta de escritório: salas divididas por paredes,
 * ligadas por portas a um corredor central. Em vez de listar cada parede à mão
 * (frágil), declara-se o CHÃO de cada sala (um rectângulo) e as PORTAS; a parede
 * é tudo o que sobra. Uma sala mal-ligada seria uma ilha inalcançável — por isso
 * `space.test.ts` faz um flood-fill do spawn e exige que TODAS as salas sejam
 * atingíveis. A geometria é validada por execução, não por confiança.
 *
 * A mobília também mora aqui, e de propósito: um móvel que BLOQUEIA passagem faz
 * parte da colisão, e a colisão é contrato partilhado. Cada `Prop` diz o QUÊ e o
 * ONDE; o ASPECTO (como se desenha um sofá) é problema do cliente (`officeArt`).
 */
/** Lado do tile em pixels. Avatar e movimento são todos múltiplos disto. */
export const TILE = 32;
/**
 * Dimensão da SUPERFÍCIE. 44×52 tiles = 1408×1664 px — um piso de empresa com um
 * JARDIM largo à volta E, mais a sul, um PARQUE DE MOBILE HOMES (o estacionamento
 * da Green Village): o prédio (31×21) fica com uma cintura de relva folgada em
 * todos os lados; a sul o lago tem relva pisável de todos os lados (dá para o
 * RODEAR a pé); e para lá do lago abre-se o recinto vedado com as caravanas
 * separadas, o asfalto com lugares marcados, os carros e a placa da marca.
 *
 * Tudo o que está ACIMA desta linha é ar livre; o que está abaixo é subsolo.
 */
export const COLS = 44;
export const SURFACE_ROWS = 52;
/**
 * A grelha inteira inclui uma FAIXA SUBTERRÂNEA colada por baixo da superfície:
 * rows 52-65, o bunker de automações. Não é um segundo mapa nem um segundo mundo
 * — é a MESMA grelha, esticada para baixo. Vale a pena dizer porquê, porque a
 * alternativa parecia mais limpa e não era: um "piso 2" com o seu próprio sistema
 * de coordenadas obrigava o servidor, o protocolo de posições, a colisão, o A* e a
 * câmara a saber em que piso está cada pessoa — e isso é um campo novo em tudo o
 * que já circula na rede. Assim, uma posição continua a ser um par (x, y) e o
 * subsolo é só... mais mapa. O que separa os dois é geometria: entre a row 51 e a
 * row 52 não há nenhum tile pisável, e a única ligação são os TELEPORTS (a
 * escotilha do bunker). Ninguém desce a pé por engano.
 *
 * A superfície fica BYTE A BYTE como estava: rows 0-51 não mudam de significado.
 */
export const UPSTAIRS_TOP_ROW = 66;
/**
 * O 1.º ANDAR, e pela mesma razão do bunker: mais mapa, não outro mundo.
 *
 * *"quero que não tenha nenhum teleporte, meio que o prédio realmente tenha um
 * segundo andar, você vê a escada no primeiro andar, e quando terminar de subir,
 * as coisas do primeiro andar somem, e aparece um chão novo por cima"*.
 *
 * O pedido é sobre o que se VÊ, e o que se vê é o mesmo sítio mais alto. Por isso
 * a faixa de cima não tem geometria própria: é uma cópia da superfície (52 rows)
 * colada a partir da row 66, e o tile por cima de [c, r] é [c, r + 66] — sempre.
 * É esse alinhamento que deixa o cliente trocar de andar sem a câmara mexer um
 * píxel: soma-se o mesmo desvio ao boneco e ao scroll, e só muda o chão.
 *
 * Tudo o que é jardim continua a ser da superfície. Lá em cima só há o que está
 * DENTRO do prédio — o gabinete, a antessala, a escada e o telhado; o que se vê
 * à volta é o jardim de baixo, mostrado por uma segunda câmara no cliente.
 */
export const UPSTAIRS_OFFSET_ROWS = UPSTAIRS_TOP_ROW;
export const ROWS = UPSTAIRS_TOP_ROW + SURFACE_ROWS;
export const MAP_WIDTH = COLS * TILE;
export const MAP_HEIGHT = ROWS * TILE;
/** Este tile está no subsolo (a faixa do bunker) e não à superfície? */
export function isUnderground(row) {
    return row >= SURFACE_ROWS && row < UPSTAIRS_TOP_ROW;
}
/** Este tile está no 1.º andar? */
export function isUpstairs(row) {
    return row >= UPSTAIRS_TOP_ROW;
}
/**
 * Cadência de rede, partilhada para o protocolo ser explícito dos dois lados.
 *
 * O cliente move o jogador local a 60fps mas só ENVIA a sua posição a cada
 * `POSITION_SYNC_MS`; os remotos são interpolados entre estes pontos, portanto
 * 10Hz chega para parecer fluido gastando uma fracção dos pacotes.
 * O servidor descarta o que chegar mais rápido que `POSITION_MIN_INTERVAL_MS`
 * — um cliente a despejar posições a 1kHz só se afoga a si próprio.
 */
export const POSITION_SYNC_MS = 100;
export const POSITION_MIN_INTERVAL_MS = 50;
/**
 * Intervalo mínimo entre dois toques no ferrolho da MESMA sessão. Mexer numa
 * porta é um gesto humano — uma vez, e vê-se o resultado; um cliente alterado a
 * alternar a tranca em ciclo fechado não seria um jogador, seria um estroboscópio
 * na cara de toda a gente no espaço (cada toque é uma escrita no Redis e um
 * broadcast a todos). Meio segundo não se sente a jogar e fecha a amplificação.
 */
export const LOCK_MIN_INTERVAL_MS = 500;
/**
 * O prédio é um rectângulo CENTRADO no mapa — NÃO ocupa a grelha toda. A cintura
 * de tiles que sobra à volta (fora deste rectângulo) é JARDIM (relva, árvores,
 * lago): scenery, não chão pisável. Assim o mundo deixa de ser uma "prisão" de
 * paredes até à borda e o prédio ganha exterior e janelas. As paredes-perímetro
 * do prédio são as bordas deste rectângulo; o interior divide-se nas salas.
 */
export const BUILDING = { col: 3, row: 3, w: 31, h: 21 };
/**
 * O lago no relvado a sul do prédio — uma elipse em coordenadas de tile. Vive AQUI
 * (não só no cliente) porque o jardim é PISÁVEL: a água tem de bloquear a colisão,
 * e a colisão é contrato partilhado. `buildWalls` usa-o para vedar o lago; o
 * cliente usa-o para saber ONDE assentar a arte da água.
 *
 * A elipse é a MOLDURA do sprite do lago: cols 9,5-23,5 × rows 26-33, ou seja
 * 448×224 px a partir de (304, 832). Os dois raios são meios-tiles exactos de
 * propósito — a arte é reduzida uma única vez para esse tamanho e desenhada 1:1,
 * sem reamostragem no browser.
 *
 * Fica com folga de relva PISÁVEL de todos os lados: rows 24-25 entre a água e a
 * parede sul do prédio (row 23), rows 33-34 até à cerca (row 35) e nove tiles a
 * este e a oeste — dá para o rodear a pé.
 *
 * A razão 2:1 não é a do render (1,52:1). É deliberada: o mundo vê-se de cima em
 * escorço, portanto um lago redondo visto a prumo TEM de comprimir na vertical
 * para assentar no chão. Não comprimir é que o faria flutuar.
 */
export const LAKE = { cx: 16.5, cy: 29.5, rx: 7, ry: 3.5 };
/**
 * A fila da CERCA — a linha que separa o jardim do pátio de mobile homes.
 *
 * Daqui para baixo é o recinto vedado: cerca de madeira a norte (esta fila, com
 * o portão ao centro), muro de pedra a sul (row 50), e lá dentro o
 * estacionamento e as quatro casas. Uma fila e não um rectângulo porque a cerca
 * atravessa o mundo de lado a lado — não há nada a sul dela que não seja o
 * pátio, e um rectângulo obrigava a manter colunas a par das do `PROPS`.
 *
 * É a MESMA fila em que as peças `fence` assentam, lá em baixo. Se a cerca
 * mudar de sítio, muda aqui — e é por isso que o valor está NOMEADO em vez de
 * escrito à mão nos dois sítios.
 */
export const PARK_ROW = 35;
export function inLake(col, row) {
    const dx = (col + 0.5 - LAKE.cx) / LAKE.rx;
    const dy = (row + 0.5 - LAKE.cy) / LAKE.ry;
    return dx * dx + dy * dy <= 1;
}
/**
 * As sete divisões do piso, agora COMPACTAS (9×7 em vez de 12×10) e todas dentro
 * do rectângulo do prédio. Os rectângulos NÃO se tocam (há sempre ≥1 tile de
 * parede entre eles); o corredor central (`hall`) atravessa a largura do prédio e
 * é a artéria que liga todas as salas através das portas.
 */
export const ROOMS = [
    { id: "office-a", name: "Vendas", col: 4, row: 4, w: 9, h: 7, floor: "wood" },
    { id: "meeting", name: "Sala de Reunião", col: 14, row: 4, w: 9, h: 7, floor: "carpet" },
    { id: "office-b", name: "TI", col: 24, row: 4, w: 9, h: 7, floor: "wood" },
    { id: "hall", name: "", col: 4, row: 12, w: 29, h: 3, floor: "lobby" },
    // A fila sul é a parte COMUM da casa — o descanso, a entrada e o café. Tem
    // porta (bate-se-lhe), mas `noBolt`: nenhuma delas tranca. Um ferrolho
    // aqui não protegia reunião nenhuma, só deixava quem chegasse primeiro fechar a
    // recepção ou a máquina do café à casa toda.
    { id: "lounge", name: "Sala de Descanso", col: 4, row: 16, w: 9, h: 7, floor: "lounge", noBolt: true },
    { id: "reception", name: "Recepção", col: 14, row: 16, w: 9, h: 7, floor: "lobby", noBolt: true },
    { id: "kitchen", name: "Copa", col: 24, row: 16, w: 9, h: 7, floor: "tile", noBolt: true },
    // O estacionamento do parque de mobile homes — a laje de asfalto no meio do
    // recinto sul. `name: ""` de propósito: é zona neutra (não dispara call), mas
    // continua a ser CHÃO (asfalto) e tem de ser alcançável a pé pelo portão da cerca.
    { id: "parking", name: "", col: 12, row: 41, w: 20, h: 7, floor: "asphalt" },
    // ── Subsolo: o bunker de automações ─────────────────────────
    // Desce-se pela escotilha do relvado este e chega-se AQUI, ao patamar do fundo
    // da escada: um cubículo de betão, `name: ""` (zona neutra, não dispara call) e
    // deliberadamente VAZIO. É o vestíbulo que dá sentido à descida — sai-se de um
    // buraco no chão para um sítio fechado e só DEPOIS se abre a porta da sala.
    { id: "bunker-hall", name: "", col: 21, row: 52, w: 4, h: 3, floor: "concrete" },
    // A sala grande: os data centers às paredes e a máquina ao centro. Tem nome,
    // logo entra automaticamente em `OFFICE_VOICE_ROOMS` — quem desce ao bunker cai
    // na call "Automações" pela mesma regra de sempre, sem uma linha de excepção.
    // Largura ÍMPAR (15): é o que põe o eixo da sala no MEIO de uma coluna, que é
    // onde a máquina central (também ímpar, 5 tiles) tem de assentar para as duas
    // filas de bastidores lhe ficarem simétricas — cinco tiles de cada lado.
    { id: "automation", name: "Automações", col: 15, row: 56, w: 15, h: 7, floor: "concrete" },
    // ── 1.º andar: o gabinete do CEO ────────────────────────────
    // Por cima da Sala de Reunião, do corredor e da Recepção — as mesmas colunas
    // (14-22), com o desvio de `UPSTAIRS_OFFSET_ROWS`. O resto do prédio fica
    // debaixo do telhado.
    //
    // A antessala está EXACTAMENTE por cima da Recepção (surface rows 16-22), que é
    // onde a escada sobe. `name: ""`: é o patamar e a sala de espera, zona neutra —
    // chega-se lá de escada e ninguém entra numa call por isso.
    { id: "ceo-lobby", name: "", col: 14, row: 82, w: 9, h: 7, floor: "carpet" },
    // O gabinete. Tem nome, portanto é canal de voz ("CEO") e tem ferrolho, pela
    // mesma regra de todas as outras salas — sem uma linha de excepção.
    { id: "ceo", name: "CEO", col: 14, row: 70, w: 9, h: 11, floor: "wood" },
];
/**
 * As salas de cargo que têm call própria — cada uma casa com um canal de VOZ de
 * nome IGUAL. Entrar na sala junta a essa call (ver OfficeSpace `onRoomChange`).
 * As zonas de trânsito (hall, parking) têm `name: ""` e por isso não entram aqui:
 * não disparam call. Esta é a FONTE ÚNICA dos nomes — o seed e o provisionamento
 * de cada servidor (servers.service) derivam daqui para não haver divergência
 * entre o que o mundo 2D mostra e os canais que existem de facto na BD.
 */
export const OFFICE_VOICE_ROOMS = ROOMS.filter((r) => r.name).map((r) => r.name);
/** O nome legível de uma sala — para os registos e para os avisos. */
export function roomName(roomId) {
    return ROOMS.find((r) => r.id === roomId)?.name ?? null;
}
/**
 * As salas a cuja porta se pode bater.
 *
 * Não é "todas as salas": é as que têm PORTA. Ficam de fora os espaços sem nome
 * — o corredor, o exterior —, e a exclusão sai da geometria (`name: ""`), não de
 * uma segunda lista que alguém tivesse de manter a par desta.
 *
 * Ter porta NÃO é ter ferrolho: quem tranca é `canLockRoom` (regra 0), que exclui
 * ainda as salas `noBolt`. São perguntas diferentes e é de propósito que são duas
 * funções — estreitar ESTA para tirar o ferrolho à Copa tirava-lhe de caminho a
 * resposta a quem bate, que ninguém pediu.
 */
export function lockableRooms() {
    return ROOMS.filter((r) => r.name !== "");
}
/**
 * O servidor a que um espaço pertence.
 *
 * O espaço do escritório É o servidor: `spaceId === serverId`, byte a byte. Isto
 * não é uma identidade gratuita — é a que dispensa qualquer mapeamento entre as
 * chaves do Redis (`gv:space:<spaceId>:occupants|locks`) e a
 * audiência ("és membro deste servidor?").
 *
 * Corta-se na mesma em `#` por uma razão de campo: houve um segundo mundo (o
 * campus 2D, retirado a 26-08-2026) cujo espaço era `<serverId>#campus`. Um
 * cliente que ainda tenha esse identificador em memória — um separador aberto
 * desde antes da remoção — cai assim no escritório com toda a gente, em vez de
 * ficar sozinho num espaço fantasma que ninguém mais vê. Preferir o mapa que
 * existe a rejeitar o pedido é o que mantém um cliente atrasado a funcionar.
 */
export function serverIdOf(spaceId) {
    const cut = spaceId.indexOf("#");
    return cut === -1 ? spaceId : spaceId.slice(0, cut);
}
/**
 * Portas — vãos de 2 tiles abertos nas divisórias, cada sala para o corredor.
 * São chão pisável: sem elas, cada sala seria uma caixa fechada. Row 11 liga as
 * salas de cima ao corredor; row 15 liga as de baixo.
 */
export const DOORS = [
    [7, 11],
    [8, 11], // Vendas ↔ corredor
    [17, 11],
    [18, 11], // Sala de Reunião ↔ corredor
    [27, 11],
    [28, 11], // TI ↔ corredor
    [7, 15],
    [8, 15], // Sala de Descanso ↔ corredor
    [17, 15],
    [18, 15], // Recepção ↔ corredor
    [27, 15],
    [28, 15], // Copa ↔ corredor
    // ── Subsolo ──
    // O vão que liga o patamar da escada à sala das automações. Fica na row 55, a
    // linha de rocha entre os dois rectângulos, alinhado com o meio do patamar
    // (cols 21-24) — entra-se e a máquina do meio está mesmo em frente.
    [22, 55],
    [23, 55], // Automações ↔ patamar do bunker
    // ── 1.º andar ──
    // Na mesma coluna da porta da Recepção lá em baixo ([17,15]/[18,15]): quem sobe
    // e se vira para norte tem a porta do gabinete à frente.
    [17, 81],
    [18, 81], // CEO ↔ antessala
];
/**
 * Portas EXTERIORES — vãos abertos na parede-perímetro do prédio para o jardim
 * ("portas para sair para fora do prédio"). São tiles pisáveis que ligam o interior
 * ao relvado sul: Sala de Descanso e Copa, junto ao lago.
 *
 * Cada vão são DOIS tiles, como o de qualquer sala («faça ela de um tamanho igual às
 * portas das salas»). Um tile só dava um buraco de 20px de largura numa parede de 32,
 * e ao lado de uma porta de sala com 64 lia-se como uma gateira.
 *
 * SÓ EM PAREDES HORIZONTAIS, e o tipo diz isso à letra — não há eixo para escolher.
 * Houve duas nos flancos do corredor (paredes verticais, folhas a correr para cima e
 * para baixo) e saíram a pedido: «vamos retirar as portas que ficam nas paredes
 * verticais, manter apenas as portas nas paredes horizontais». Com elas foi-se todo o
 * ramo vertical — o eixo, o desvio de meio tile que centrava um vão de dois tiles num
 * corredor de três, a rotação de 90° da arte. Um ramo sem um único caso a percorrê-lo
 * é código que apodrece sem ninguém dar por isso; se um dia voltar a haver uma porta
 * de flanco, volta com o seu teste.
 *
 * O prédio continua a ter saída por todos os lados: o jardim dá a volta inteira, por
 * isso quem sai por uma destas duas chega a pé a qualquer ponto da cintura de relva.
 */
export const EXTERIOR_DOOR_SPANS = [
    { col: 6, row: 23 }, // Sala de Descanso ↔ relvado sul
    // Recepção ↔ relvado sul — a porta das traseiras, e a cabeça do caminho de
    // pedra que desce ao parque (`GARDEN_PATH`). Cols 17-18 não é arbitrário: é a
    // MESMA coluna da porta interior da Recepção (`DOORS` [17,15]/[18,15]), por
    // isso quem entra do corredor atravessa a sala em linha recta e sai por trás
    // sem um único desvio. Desalinhada, a travessia obrigava a um passo de lado a
    // meio da sala — e é justamente o troço onde há o sofá e a mesa de centro.
    { col: 17, row: 23 },
    { col: 30, row: 23 }, // Copa ↔ relvado sul
];
/**
 * Os tiles de todos os vãos exteriores, achatados — é o que a colisão fura e o que
 * o cliente salta na varredura de paredes. Deriva-se dos spans para não haver duas
 * listas a divergir: acrescentar um vão é acrescentar UMA linha acima.
 */
export const EXTERIOR_DOORS = EXTERIOR_DOOR_SPANS.flatMap(({ col, row }) => [
    [col, row],
    [col + 1, row],
]);
/**
 * VÃOS SEM PORTA na parede-perímetro — «abre uma abertura para fora, não precisa
 * de porta, apenas uma abertura».
 *
 * A diferença para `EXTERIOR_DOORS` não é de tamanho, é de NATUREZA: uma porta
 * tem batente, duas folhas mascaradas, um alcance que a abre e a fecha e um
 * estado a manter todos os frames; isto é um buraco. Não é uma porta com a
 * animação desligada — é a ausência de porta. Por isso é uma lista à parte e não
 * uma flag no span: a flag obrigaria todo o código de portas a perguntar-lhe
 * "és mesmo uma porta?" em cada passo, que é a definição de tipo mal escolhido.
 *
 * Está no FIM DO CORREDOR, na parede do corredor: «não era pra ter colocado na
 * sala de ti, era pra ter colocado no final do corredor, na parede do corredor».
 * Esteve na parede este da TI (rows 9-10) e saiu de lá por isto — uma abertura
 * dentro de uma sala é a saída PRIVADA de alguém, e este vão é da casa toda.
 *
 * Concretamente: col 33 (o perímetro; o `hall` vai das cols 4 à 32), rows 12-14
 * — as TRÊS linhas do corredor, nem mais nem menos.
 *
 * Levou duas à primeira, as de baixo, e voltou: «centralize, pois ficou mais
 * para um canto». Tinha razão e não havia como afinar: DUAS tiles num corredor
 * de TRÊS caem sempre meia tile fora do eixo, para cima ou para baixo, e a
 * `plantTall` que selava a row 12 puxava o olho ainda mais para o lado oposto.
 * Centrar por tentativas era escolher qual das metades ficava torta.
 *
 * Então a boca deixou de ser um buraco a centrar na parede e passou a ser a
 * LARGURA DO CORREDOR. É a única versão que não pode ficar descentrada: não há
 * margem de um lado nem do outro para comparar. E lê-se pelo que é — o corredor
 * a continuar lá fora, não um vão aberto ao calhas no flanco. Para isso a planta
 * alta do canto recuou uma coluna (col 32 → 31): continua a fazer o verde da
 * ponta do corredor, agora AO LADO do vão em vez de à frente dele.
 *
 * O lado ESTE e não o oeste: é o que dá para o relvado onde está a escotilha do
 * bunker (cols 40-42). Sair por aqui é o caminho curto de dentro do prédio até
 * lá, em linha recta pelo corredor; pelo flanco oeste dava a um relvado sem nada
 * e obrigava a contornar o prédio inteiro.
 *
 * Nada disto é desenho: a arte lê a lista para NÃO pintar pedra por cima do
 * buraco (ver a varredura de paredes em `OfficeSpace`). Aqui só se diz onde ele
 * está e que se atravessa.
 */
export const EXTERIOR_OPENINGS = [
    [33, 12], // a boca é o corredor inteiro: rows 12-14 ↔ relvado este
    [33, 13],
    [33, 14],
];
// ── Caminho de pedra e pedrisco (Recepção → parque de mobile homes) ──
//
// Um carreiro de saibro pisado que sai da porta das traseiras da Recepção e vai
// dar ao estacionamento, lá em baixo. Não é chão novo: é CENÁRIO. O relvado do
// jardim já é pisável de ponta a ponta, portanto o caminho não abre passagem
// nenhuma — só mostra POR ONDE se passa. Daí não ser um `FloorKind`:
//   · um `floor` novo entrava na grelha partilhada, no `floorAt`, no mapa de
//     texturas e em todo o consumidor de `FloorKind` — para pintar relva de
//     outra cor;
//   · e um rectângulo em `ROOMS` (mesmo com `name: ""`) mete-se no `roomAt`, no
//     A* e na lógica de salas. Um carreiro que participe na contabilidade de
//     divisões é um bug à espera de acontecer.
// Aqui é uma lista de rectângulos que o cliente lê para pintar POR CIMA da relva.
// Zero impacto em colisão, em `roomAt` e em `OFFICE_VOICE_ROOMS` — atravessá-lo
// não entra em call nenhuma, que é exactamente o que um caminho deve fazer.
//
// O traçado tem uma cotovelada, e tem-na por obrigação: a linha recta da Recepção
// (cols 14-22) até ao portão da cerca passa por DENTRO do lago (a elipse tapa as
// cols 9-23 nas rows 26-32). Então desce-se dois tiles, contorna-se pela margem
// ESTE — a única onde o lago nunca chega, que é a col 24 — e volta-se para dentro
// já em baixo da água, a apanhar o eixo do portão (o vão livre da cerca, cols
// 19-24). Um caminho que tivesse de passar por cima de água é um caminho errado.
//
// Largura DOIS tiles em todo o percurso, a mesma de qualquer porta da casa: um
// tile ficava mais estreito que os ombros do avatar e lia-se como risco no chão.
export const GARDEN_PATH = [
    // Da soleira (row 23 = o próprio vão) até à orla norte do lago. Começar NA
    // porta e não abaixo dela é o que faz a pedra encostar à soleira em vez de
    // deixar um tile de relva a separar a casa do seu caminho.
    { col: 17, row: 23, w: 2, h: 3 },
    // Faixa a este, colada à parede sul do prédio, entre o lago e o edifício.
    { col: 19, row: 24, w: 7, h: 2 },
    // A descida pela margem este da água: cols 24-25 nunca são lago (a elipse
    // acaba na col 23) e nenhuma árvore do jardim lá assenta.
    { col: 24, row: 26, w: 2, h: 9 },
    // Já a sul do lago, de volta para dentro à procura do portão.
    { col: 21, row: 33, w: 3, h: 2 },
    // Pelo portão adentro (cols 21-22 caem no vão livre 19-24) e recta até ao
    // asfalto, que começa na row 41. Passa ao lado do letreiro do parque (cols
    // 25-27) e dos candeeiros (cols 11 e 31).
    { col: 21, row: 35, w: 2, h: 6 },
];
/** Chave de tile para os conjuntos deste ficheiro — `col` cabe em 0..43. */
function tileKey(col, row) {
    return row * COLS + col;
}
const PATH_TILES = (() => {
    const set = new Set();
    for (const { col, row, w, h } of GARDEN_PATH)
        for (let r = row; r < row + h; r += 1)
            for (let c = col; c < col + w; c += 1)
                set.add(tileKey(c, r));
    return set;
})();
/** Este tile é carreiro de pedrisco? Só o cliente pergunta — é puro cenário. */
export function onGardenPath(col, row) {
    return PATH_TILES.has(tileKey(col, row));
}
export const TELEPORTS = [
    // A pegada EXACTA da tampa (ver `PROPS`: `bunkerHatch` em [40,3], 3×2). É a
    // mesma área que se vê, e é de propósito que não tem folga nenhuma à volta: o
    // aviso aparece quando os pés estão em cima do aço, nunca antes. Chega-se ao
    // patamar na coluna ao lado da escada — em frente está a porta da sala.
    { id: "bunker-down", zone: { col: 40, row: 3, w: 3, h: 2 }, to: [23, 53], label: "Entrar no bunker", auto: false },
    // O patamar TODO (o rectângulo de `bunker-hall`). Aqui a folga é o ponto: num
    // cubículo de betão onde a escada não se distingue do chão, exigir o tile certo
    // era o que prendia as pessoas lá em baixo. Sai-se uma linha a SUL da tampa —
    // em frente é o jardim aberto, não a boca do buraco nem a borda do mapa.
    //
    // `auto`: o patamar é um beco com uma porta só, logo chegar aqui É o pedido de
    // sair. Basta pôr lá o pé e sobe-se — sem tecla, que é o que foi pedido.
    { id: "bunker-up", zone: { col: 21, row: 52, w: 4, h: 3 }, to: [41, 5], label: "Sair para o jardim", auto: true },
];
/** A escotilha ao alcance da mão, ou `null` — decide o aviso e o que a tecla E faz. */
export function teleportNear(px, py) {
    if (px < 0 || py < 0 || px >= MAP_WIDTH || py >= MAP_HEIGHT)
        return null;
    const col = Math.floor(px / TILE);
    const row = Math.floor(py / TILE);
    for (const t of TELEPORTS) {
        if (inRect(col, row, t.zone))
            return t;
    }
    return null;
}
/** O meio da boca, em pixels — onde o cliente põe a marca e o nome do destino. */
export function teleportCenter(t) {
    return { x: (t.zone.col + t.zone.w / 2) * TILE, y: (t.zone.row + t.zone.h / 2) * TILE };
}
/** Onde o corpo assenta do outro lado — o centro do tile de chegada, em pixels. */
export function teleportDestination(t) {
    return { x: (t.to[0] + 0.5) * TILE, y: (t.to[1] + 0.5) * TILE };
}
/**
 * A escada da Recepção: degraus nas cols 14-15, rows 20-22, a subir para norte.
 * É a pegada de degraus das peças `stairs` (em baixo) e `stairwell` (em cima) —
 * há um teste a prender as três no mesmo sítio.
 */
export const ESCADAS = [{ id: "recepcao", col: 14, w: 2, topo: 20, fundo: 22 }];
/**
 * Pisar este tile troca de andar? Devolve o tile de chegada, ou `null`.
 * É a MESMA regra para o passo do cliente, para o A* e para os testes.
 */
export function destinoDaEscada(col, row) {
    for (const e of ESCADAS) {
        if (col < e.col || col >= e.col + e.w)
            continue;
        if (row === e.topo)
            return [col, row + UPSTAIRS_OFFSET_ROWS];
        const emCima = row - UPSTAIRS_OFFSET_ROWS;
        if (emCima >= e.topo + 2 && emCima <= e.fundo)
            return [col, emCima];
    }
    return null;
}
/**
 * A troca de andar em pixels: o mesmo x, o y com o desvio exacto. Sem arredondar
 * ao centro do tile de propósito — o boneco não pode dar um salto no ecrã.
 */
export function trocaDeAndar(px, py) {
    const col = Math.floor(px / TILE);
    const row = Math.floor(py / TILE);
    const destino = destinoDaEscada(col, row);
    return destino ? { x: px, y: py + (destino[1] - row) * TILE } : null;
}
/**
 * Pegada fixa de cada tipo: tamanho em tiles + se bloqueia passagem.
 *
 * `recorte` existe porque uma peça desenhada em 3/4 é um PARALELOGRAMO e a
 * pegada é um rectângulo: os cantos que ficam fora do escorço não têm arte
 * nenhuma por cima e, bloqueados na mesma, são a parede invisível — bate-se a
 * dois tiles da casa em chão de relva à vista. Medido no PNG, tile a tile, a
 * `mobileHome` tinha DOIS tiles da fila da frente com 0% de arte e a `Arch`
 * tinha quatro. Encolher o rectângulo não resolve (nenhum rectângulo cabe num
 * paralelogramo sem deixar de fora parede a sério), por isso a peça diz que
 * tiles é que ocupa: uma linha por fila, de CIMA para baixo, `#` bloqueia e `.`
 * deixa passar. Sem `recorte`, bloqueia-se a pegada inteira como sempre.
 *
 * Regra de quem escreve um — e NÃO é "quanto do tile tem arte por cima", que foi
 * a primeira e deu casas atravessáveis. A casa desenha-se a `depth = (row+h)*TILE`
 * e o avatar a `depth = y` dos pés (`officeAvatars.ts`); DENTRO da pegada os pés
 * estão sempre acima dessa linha, logo a casa fica SEMPRE à frente do boneco. Um
 * tile libertado com arte por cima do CORPO não deixa encostar: engole.
 *
 * Mede-se então a caixa do boneco — 26 px de largo (a mesma elipse de sombra que
 * o jogo desenha) por 54 px de alto (do chão à etiqueta do nome) — de pé no
 * centro do tile, no pior caso vertical. **O tile só se liberta se ficar <25%
 * tapado.** Um quarto do corpo escondido lê-se como estar atrás de uma esquina;
 * mais do que isso lê-se como estar dentro da casa.
 *
 * O preço está aceite e é conhecido: sobra chão vazio bloqueado sob o beiral, no
 * canto do escorço (o pior é 0% de arte). É o mal menor — bate-se onde o telhado
 * está por cima da cabeça, em vez de desaparecer dentro da parede.
 */
export const PROP_SPECS = {
    // Secretária do SUPERVISOR da TI — a única do mapa. Nasceu a 2 tiles com a arte
    // espremida a 64×56: o render do pacote tem proporção 1,7985 e 64×56 dá 1,143,
    // ou seja a mesa estava achatada a 64 de largo quando lhe cabiam 96. Via-se
    // desde que a cadeira de escritório nova (32×40) foi para debaixo dela — metade
    // da largura da mesa era cadeira. E era o supervisor a ter a MENOR secretária da
    // sala: as consolas dos funcionários têm 90 px de arte, contra os 64 dele.
    //
    // A 3 tiles a arte fica 96×54 (a proporção verdadeira, desvio de -1,15%), e a
    // largura ÍMPAR paga-se sozinha: a cadeira está na col 31, que passa a ser a col
    // do MEIO da mesa, logo o `seatAnchor` não tem nada para corrigir (offset 0, e
    // não os 16 px que a largura par obrigava a encaixar). Continua abaixo do que
    // uma bancada: 54 px de alto para um avatar de 48, contra os 70 que Vendas
    // provou serem "mt exagerado".
    deskPC: { w: 3, h: 1, solid: true },
    // Secretária de Vendas: a mesma peça de mobiliário que a `deskPC`, mas com a
    // arte do pacote (`sales-desk-v1.png`) — telefone à esquerda, teclado e rato ao
    // centro, torre encaixada, quatro gavetas, em escorço e com proporção 1,383.
    //
    // Nasceu a 3 tiles, com 96×70 px de arte, e o argumento era que a 2 tiles ficava
    // MAIS BAIXA do que os 64×56 do desenho de código que vinha substituir. O
    // argumento estava certo e a conclusão errada: em jogo, 96×70 é a maior peça de
    // mobiliário do escritório e passa a cabeça de um avatar de 32×48 — parece uma
    // bancada, não uma secretária. **A referência não é o desenho antigo, é o boneco
    // que se senta lá.** A 2 tiles a arte fica 64×46 (dois avatares de largo, uma
    // cabeça acima do tampo) e continua a ler-se inteira: o ficheiro foi reduzido de
    // novo, em offline e ao pixel final, e o telefone e o teclado sobrevivem.
    //
    // A largura PAR traz de volta o desvio que os 3 tiles evitavam — o meio da mesa
    // cai na fronteira entre os dois tiles, logo o lugar nasce 16 px ao lado. Foi o
    // caso da `deskPC` até ela passar a 3 tiles: o `seatAnchor` corrige
    // 15 dos 16 (`SEAT_NUDGE_MAX` = meio tile menos um pixel, para o corpo não sair
    // do tile do próprio lugar) e sobra 1 px, que não se vê. O que NÃO se pode fazer
    // é alargar a pegada e deixar a cadeira na ponta: a 4 tiles o desvio ia a 48 px,
    // o encaixe só tapava 15 e a pessoa trabalhava na quina. O teste
    // "serve um lugar, e ele fica ao centro do monitor" guarda exactamente isso.
    salesDesk: { w: 2, h: 1, solid: true },
    // Consola de seis monitores: a arte tem 90×48 px para uma pegada de 96×32, logo
    // os 16 px que sobram transbordam para CIMA e é a bancada que fica à frente da
    // cadeira que tem atrás — não é a cadeira que aparece por cima dos monitores.
    // A largura ÍMPAR não é estética: com o desenho centrado em `baseX`, só um
    // número ímpar de colunas põe o ÚNICO lugar exactamente no meio da consola, que
    // é onde o pacote desenhou a cadeira (medida: x≈59,5 num recorte de 120).
    deskDual: { w: 3, h: 1, solid: true },
    officeChair: { w: 1, h: 1, solid: false },
    // Mesa de reunião: 96×64 de pegada para 96×72 de arte. Os 8 px a mais sobem e
    // encostam a fila norte de cadeiras ao tampo, sem as engolir.
    meetingTable: { w: 3, h: 2, solid: true },
    meetingChair: { w: 1, h: 1, solid: false },
    whiteboard: { w: 4, h: 1, solid: false },
    // O sofá NÃO é sólido: é um assento. Bloqueá-lo tornaria impossível sentar-se
    // nele — ver a secção "Sentar" mais abaixo (pisar o assento = sentar).
    couch: { w: 3, h: 1, solid: false },
    // Sofá de perfil: a MESMA peça de três lugares com os eixos trocados — 1 coluna
    // de largura por 3 linhas de fundo. A pegada é 1×3 e não 2×3 por causa de uma
    // regra deste ficheiro: cada tile de pegada de um assento É um lugar (ver
    // `SEATS`). A 2×3 o sofá passava a servir SEIS pessoas, o dobro das almofadas
    // que tem desenhadas — e três delas sentavam-se no vazio ao lado do sofá.
    // A profundidade que falta à pegada, a arte ganha-a a transbordar para os
    // lados (não é sólido, portanto o transbordo não faz parede invisível).
    couchSide: { w: 1, h: 3, solid: false },
    coffeeTable: { w: 2, h: 1, solid: true },
    tv: { w: 3, h: 1, solid: false },
    rug: { w: 4, h: 3, solid: false },
    // Balcão de recepção: 3 tiles, não 4, desde que passou a ser o PNG do pacote.
    // A arte final mede 96 px — encaixa ao pixel em 3 tiles e transbordar para os
    // LADOS é o transbordo perigoso (é a parede invisível de que já houve queixa).
    //
    // A 3 tiles o balcão ocupa as cols 16-18 e o seu meio cai em 16·32+48 = 560; a
    // cadeira da recepcionista (col 17) tem centro em 17·32+16 = 560. Desvio ZERO,
    // que é o caso ideal de uma pegada ÍMPAR com a cadeira na coluna do meio — a 4
    // tiles ficavam 16 px por tapar com o empurrão de assento. E encolher liberta a
    // col 19: menos bloqueio do que antes, nunca mais.
    receptionDesk: { w: 3, h: 1, solid: true },
    plant: { w: 1, h: 1, solid: true },
    plantTall: { w: 1, h: 1, solid: true },
    counter: { w: 4, h: 1, solid: true },
    fridge: { w: 1, h: 1, solid: true },
    diningTable: { w: 2, h: 2, solid: true },
    diningChair: { w: 1, h: 1, solid: false },
    waterCooler: { w: 1, h: 1, solid: true },
    bookshelf: { w: 2, h: 1, solid: true },
    // Sólido: assenta no chão e esbarra-se nele, como o bebedouro. A arte sobe 36 px
    // acima do tile (24×68) — é a caixa alta a subir pela parede, não o pé a
    // avançar para o chão de quem passa.
    grandfatherClock: { w: 1, h: 1, solid: true },
    // Mesa de jogos: sólida como qualquer mesa (contorna-se, não se atravessa) e
    // com 2×1 de pegada para os dois lugares ficarem um de cada lado do tampo em
    // vez de na mesma aresta. O tabuleiro em pé sobre o tampo sobe 20 px acima da
    // pegada — é o que dá a leitura de "está ali um jogo em cima da mesa" sem a
    // mesa passar a ocupar duas linhas de chão.
    gameTable: { w: 2, h: 1, solid: true },
    // Máquina de arcade: ao contrário do quadro branco ou da TV, esta ASSENTA no
    // chão — logo é sólida, como qualquer outro móvel em que se esbarraria. Um
    // tile de pegada e 70 px de arte: os 38 que sobram são o armário a subir por
    // cima do tile, que é o que faz uma arcade parecer uma arcade e não um caixote.
    arcade: { w: 1, h: 1, solid: true },
    // ── Bunker ──
    // Bastidor: 32×64 de arte para 32×32 de pegada. Ocupa um tile no chão e sobe o
    // dobro — é a mesma leitura do `plantTall`, e é o que faz uma fila de bastidores
    // encostada à parede parecer alta em vez de parecer uma caixa.
    serverRack: { w: 1, h: 1, solid: true },
    // A máquina do meio: 160×102 de arte sobre 160×64 de pegada. Largura ÍMPAR pela
    // mesma razão da consola da TI — com o desenho centrado em `baseX`, é a única
    // maneira de a máquina ficar no eixo exacto da sala e do vão da porta.
    aiConsole: { w: 5, h: 2, solid: true },
    // Ecrã de parede: não é sólido, tal como a TV da Sala de Descanso — está pendurado.
    analyticsScreen: { w: 3, h: 1, solid: false },
    // A escotilha é chão: pisa-se de propósito (é o teleporte). Sólida seria uma
    // tampa que nunca abre — exactamente o contrário do que foi pedido.
    bunkerHatch: { w: 3, h: 2, solid: false },
    // A escada da Recepção, vista de baixo (`stairs`) e de cima (`stairwell`). A
    // pegada é a mesma nos dois andares — 2 colunas de degraus e 1 de corrimão, 3
    // filas de degraus e 1 de remate — e o recorte diz o que se atravessa:
    //   · em baixo, a fila de cima é a laje do 1.º andar (não se passa por baixo
    //     dela) e a fila de baixo é o chão onde se chega à escada, pelo lado;
    //   · em cima, a fila de cima é o patamar, e o corrimão fecha o poço até à
    //     parede sul — só se entra na escada de frente.
    stairs: { w: 3, h: 4, solid: true, recorte: ["##.", "..#", "..#", "..."] },
    stairwell: { w: 3, h: 4, solid: true, recorte: ["...", "..#", "..#", "..#"] },
    // A casa do lago: 6×3 para uma arte de 192×98. Sobram 2 px acima da pegada —
    // quase nada, porque o render é de 3/4 muito rasante e o telhado mal se
    // levanta do chão que ocupa. `solid` é redundante para a colisão (a água já
    // está vedada por `inLake`) mas é a verdade sobre a peça, e é o que continua a
    // valer se um dia o lago passar a ser atravessável de barco.
    floatingHouse: { w: 6, h: 3, solid: true },
    // ── Parque de mobile homes ── (caravanas e carros bloqueiam; cones,
    // floreiras e as linhas de asfalto deixam-se pisar/atravessar).
    // 5×4 para uma arte de 160×148: o telhado sobe 20 px ACIMA da pegada e passa
    // por cima da cabeça de quem anda encostado, que é o que se espera de um
    // beiral. Encolheu duas vezes — 7×4/224×208, depois 6×4/192×178 e agora esta.
    // A comparação que a justifica está em pixels de PAREDE (do beiral ao chão),
    // não em impressão: eram 76 px contra os 67 da `Arch` ao lado, e 201 px de
    // silhueta contra 160 (26% mais alta na MESMA projecção de 3/4). Ao factor
    // 148/208 a parede dá 54 px e a silhueta 143 — a v1 passou a ser a casa mais
    // pequena do parque, que é o que foi pedido.
    //
    // NB: o comentário que aqui estava dizia que "a altura da parede é igual nas
    // três" e que as três tinham crescido pelo mesmo ≈1,4×. Medindo o PNG, as duas
    // afirmações eram falsas — é o que dava a casa fora de escala.
    //
    // O `recorte` liberta UM tile — o canto noroeste, o fundo do escorço, com 6%
    // de arte por cima e 3% do corpo tapado. Chegou a libertar cinco pela regra
    // dos 35% de arte, e era por aí que se entrava dentro da casa: o tile da fila
    // da frente à direita tem 0% de arte MAS tapa 62% do boneco, porque o corpo
    // sobe 54 px e a parede está lá em cima. Arte por cima do CHÃO e arte por cima
    // do CORPO são duas medidas diferentes; a que manda é a segunda.
    mobileHome: { w: 5, h: 4, solid: true, recorte: [".####", "#####", "#####", "#####"] },
    mobileHomeFlip: { w: 5, h: 4, solid: true, recorte: ["####.", "#####", "#####", "#####"] },
    // A `Arch` mantém 7×4 — o tamanho dela está bom, só o recorte é que estava
    // errado. Sobra-lhe telhado por cima (arte 224×160 sobre 224×128) porque o
    // arco sobe mais do que a água de telhado plana da outra. Libertava seis tiles,
    // três deles na fila da frente a taparem 80%, 100% e 100% do corpo — era esta
    // (a `ArchFlip`, junto à cerca leste) a casa onde só se via a etiqueta do nome
    // a flutuar. Fica com o mesmo canto noroeste: 8% de arte, 4% de corpo tapado.
    mobileHomeArch: { w: 7, h: 4, solid: true, recorte: [".######", "#######", "#######", "#######"] },
    mobileHomeArchFlip: { w: 7, h: 4, solid: true, recorte: ["######.", "#######", "#######", "#######"] },
    // O alçado lateral é o caso oposto e a pegada tem de o dizer: 9 tiles de
    // comprido porque uma casa vista de lado mostra-se INTEIRA (as de 3/4 estão
    // em escorço, por isso medem menos), e 3 de fundo porque a arte tem 100 px de
    // alto — dar-lhe 4 (128 px) punha a bloquear 28 px de chão onde não está
    // desenhado nada, que é a parede invisível clássica. Com h=3 sobram 4 px de
    // beiral acima da pegada, que é quanto um alçado sem plano de telhado dá.
    // Sem `recorte`, e medida: um alçado não tem escorço, logo não tem cantos
    // vazios — o tile mais fraco dos 27 tapa 27% do corpo, acima do limiar de 25%.
    // A pegada cheia é aqui a verdade da peça.
    mobileHomeSide: { w: 9, h: 3, solid: true },
    mobileHomeSideFlip: { w: 9, h: 3, solid: true },
    // ── As quatro casas novas ──
    // São QUATRO e não mais: os ficheiros são os que estão no ambiente de trabalho
    // (`casa_capsula_elevada_branca`, `casa_capsula_portas_fechadas`, `casa_domo…`,
    // `casa_modular_madeira…`). Duas rendem as do canto oeste do parque, duas sobram
    // e vão para o relvado a nascente do lago. Quem contar mais está a contar o
    // arquivo de onde vieram, não o pedido.
    //
    // Todas 3 de fundo porque a arte foi ASSADA para isso: o ficheiro sai do forno
    // com a altura que a proporção da caixa opaca manda, e as quatro caem entre 96 e
    // 152 px sobre os 96 de três tiles. Acima (a `Dome`, 152) o excedente sobe, e
    // subir é de graça (`setOrigin(0.5, 1)`). Dar-lhes 4 de fundo era bloquear até
    // 32 px de relva sem nada desenhado por cima: a parede invisível clássica, a
    // queixa que já custou duas correcções.
    //
    // Nenhuma leva `recorte`, e isso foi MEDIDO, não assumido: o recorte existe para
    // o paralelogramo do escorço de 3/4, que deixa cantos da pegada com zero arte por
    // cima do corpo. Estas ou são volumes cheios de ponta a ponta (as duas cápsulas,
    // o modular) ou têm a base mais larga do que o corpo (a cúpula), e o pior canto
    // das quatro tapa 31% do corpo do avatar — acima do limiar de 25% que liberta.
    // A cúpula é 4 e não 5 por uma razão que se vê melhor do que se explica: ela é
    // desenhada de uma câmara MAIS ALTA do que o resto do parque — mostra o chão de
    // madeira lá dentro, enquanto as vizinhas mostram paredes. Num sítio onde tudo o
    // resto se vê de lado, um objecto visto de cima lê-se como estando NO AR, e a
    // 160 px passava 56 px acima da própria pegada (as vizinhas passam 0 a 20) — o
    // triplo da folga com a menor área de chão de todas. Encolhida para 128 px passa
    // 26, que é a mesma ordem das outras, e volta a ler-se como uma casa pousada.
    homeDome: { w: 4, h: 3, solid: true },
    homeCapsuleClosed: { w: 5, h: 3, solid: true },
    homeModular: { w: 6, h: 3, solid: true },
    homeCapsule: { w: 7, h: 3, solid: true },
    // Os carros são 2×4 e a pegada é a arte inteira: o ficheiro tem 64×128 px e
    // vista de cima não há escorço nenhum, portanto o rectângulo que se vê é o
    // rectângulo que se bloqueia. 2 tiles = 2,3 m de largura e 4 = 4,7 m de
    // comprimento à escala do mundo (o boneco tem 48 px para 1,75 m), que são as
    // medidas de um carro a sério.
    carSedan: { w: 2, h: 4, solid: true },
    carSport: { w: 2, h: 4, solid: true },
    carSuper: { w: 2, h: 4, solid: true },
    carSuv: { w: 2, h: 4, solid: true },
    streetLamp: { w: 1, h: 1, solid: true },
    parkSign: { w: 3, h: 1, solid: true },
    dumpster: { w: 2, h: 1, solid: true },
    trashCan: { w: 1, h: 1, solid: true },
    cone: { w: 1, h: 1, solid: false },
    // O canteiro era 1×1 e ATRAVESSÁVEL, e as duas coisas estavam erradas pela
    // mesma razão: a pegada foi escrita a pensar no desenho de 32×32 que ele teve
    // um dia, e a arte que se vê tem 64 px de frente e um muro de pedra de 27 px
    // de alto. Passava-se a pé por dentro de alvenaria. Agora 2×1 e sólido — o
    // chão que bloqueia é exactamente o que a arte tapa, nem mais um tile.
    flowerBox: { w: 2, h: 1, solid: true },
    fence: { w: 4, h: 1, solid: true },
    stoneWall: { w: 4, h: 1, solid: true },
    // Um bloco de riscas = TRÊS baias de 2 tiles com 1 tile de asfalto entre
    // elas, e o fundo passou de 3 para 4 tiles para bater com o comprimento do
    // carro. Antes eram 4×3, medida herdada das carrinhas: um carro de 4 tiles
    // pousado numa baia de 3 ficava com o capô 32 px acima do batente pintado —
    // lia-se como mal estacionado, que é o mesmo defeito que já está anotado
    // mais abaixo, na fila.
    parkingLines: { w: 6, h: 4, solid: false },
};
/**
 * Cada móvel do piso, sala a sala. A ordem não importa (o cliente ordena por
 * profundidade). Os `solid` entram na grelha de colisão; os decorativos (cadeiras,
 * tapetes, TV, quadro branco) deixam-se atravessar/pisar.
 */
export const PROPS = [
    // Três regras arrumam o piso todo, e são elas que este bloco cumpre à letra:
    //   1. cada assento está ENCOSTADO à mesa a que serve (é daí que sai a direcção
    //      em que a pessoa se senta — ver `facingFor`, mais abaixo). Uma cadeira
    //      solta no meio da sala não existe: seria alguém sentado a olhar a parede;
    //   2. a faixa de chão do rótulo da sala (`ROOM_LABELS`) fica VAZIA — nada de
    //      nomes de sala escondidos atrás de um balcão ou de um quadro;
    //   3. a linha entre a porta e o miolo da sala fica desimpedida: entra-se e
    //      anda-se, sem contornar mobília logo à entrada.
    // ── Vendas: três postos ENCOSTADOS à parede norte; o resto da sala fica livre ──
    //
    // A fila está na row 4, a primeira da sala, e continua lá por segurança e não
    // por gosto. Quando os postos ocupavam 3 tiles não havia escolha nenhuma: três
    // vezes 3 enchem ao tile as 9 colunas da sala (4 a 12) e formavam uma barreira
    // sólida de parede a parede, que numa row interior SELA tudo o que fique do
    // lado de lá — com a fila na row 6 as rows 4-5 ficavam sem caminho desde a
    // porta, que é a sul em `[8, 11]`. Foi o teste "chega do spawn a qualquer sala"
    // que apanhou isso. A 2 tiles a fila já deixa buracos (cols 6, 9 e 12) e a
    // barreira desapareceu, mas a row 4 mantém-se: é a única linha onde não há
    // "lado de lá" nenhum (a norte é a parede do prédio), logo é a única onde
    // acrescentar mobília nunca pode voltar a fechar caminho.
    //
    // A altura da arte não é problema nesta linha e há precedente medido: a
    // `plantTall` que aqui estava sobe 72 px acima da base, muito mais do que os 46
    // desta secretária, e sempre se desenhou por cima da parede norte sem incomodar.
    { kind: "salesDesk", col: 4, row: 4 },
    { kind: "salesDesk", col: 7, row: 4 },
    { kind: "salesDesk", col: 10, row: 4 },
    // As cadeiras ficam ATRÁS da secretária (row 5 = a sul): quem trabalha vira-se
    // para o monitor, ou seja para NORTE. É a regra 1 no caso mais simples.
    //
    // A coluna é a SEGUNDA das duas de cada posto, e podia ser a primeira — numa
    // pegada par o meio da mesa cai na fronteira entre os dois tiles, logo o lugar
    // nasce 16 px ao lado seja de que lado for, e o `seatAnchor` corrige 15 dos 16
    // em qualquer dos casos. O que a cadeira não pode é sair das colunas do posto.
    // Ver o comentário da largura par em `PROP_SPECS.salesDesk`.
    { kind: "officeChair", col: 5, row: 5 },
    { kind: "officeChair", col: 8, row: 5 },
    { kind: "officeChair", col: 11, row: 5 },
    // O QUARTO posto, numa segunda fila. A de cima já ocupava a parede toda
    // (colunas 4-12, três postos de dois com um vão entre cada), por isso este
    // desce para a sala aberta — e fica EXACTAMENTE por baixo do PRIMEIRO, nas
    // mesmas colunas, para se ler como uma segunda fila e não como uma mesa solta.
    //
    // Esteve por baixo do terceiro (cols 10-11) e passou para a ESQUERDA a pedido.
    // Nunca ao meio: o meio (7-8) é o corredor que sobe da porta, e uma secretária
    // ali obrigava toda a gente a contorná-la para entrar. Mesma regra do resto da
    // fila: a cadeira fica a SUL e quem se senta olha para norte, para o monitor.
    { kind: "salesDesk", col: 4, row: 7 },
    { kind: "officeChair", col: 5, row: 8 },
    // A planta alta e o relógio de pé ficam na row 6, um em cada parede lateral:
    // emolduram a sala aberta que a fila encostada libertou (rows 6 a 10).
    //
    // O relógio entrou para o lugar da ESTANTE (cols 11-12), retirada a pedido. É
    // de um tile só, e fica na col 12, encostado à parede da direita — a col 11
    // passa a estar livre, o que só acrescenta caminho à volta do quarto posto.
    { kind: "plantTall", col: 4, row: 6 },
    { kind: "grandfatherClock", col: 12, row: 6 },
    { kind: "plant", col: 4, row: 9 },
    // REGRA DOS BEBEDOUROS: ficam SEMPRE encostados à parede da DIREITA da sala,
    // isto é na última coluna dela (aqui a 12, das 4-12 de Vendas). Vale para as
    // salas que têm um — Vendas, Recepção e Copa (o da TI saiu a pedido) — e o sítio de cada um
    // confere-se com a `col` da sala mais a `w` menos 1, não a olho. Este era o
    // único que já cumpria a regra; os outros três vieram todos da parede esquerda.
    //
    // Não é decoração: um bebedouro é sólido e não se atravessa. Encostado à parede
    // do fundo tira um tile de um sítio onde já não se passa; no meio de uma parede
    // interior ou junto à porta, tirava-o de um caminho.
    { kind: "waterCooler", col: 12, row: 9 },
    // ── Sala de Reunião: mesa ao centro, DEZ lugares encostados a ela ──
    // A mesa ocupa cols 17-19 / rows 6-7 — o centro geométrico exacto da sala (cols
    // 14-22 → centro 18; rows 4-10 → centro 7). Os dez lugares fazem-lhe a volta
    // completa: três a norte, três a sul e DOIS em cada topo. Nenhum fica a um tile
    // de distância — quem se senta está mesmo à mesa, não ao pé dela.
    { kind: "plantTall", col: 14, row: 4 },
    { kind: "whiteboard", col: 16, row: 4 },
    { kind: "plantTall", col: 22, row: 4 },
    { kind: "meetingChair", col: 17, row: 5 }, // fila norte → olham para SUL
    { kind: "meetingChair", col: 18, row: 5 },
    { kind: "meetingChair", col: 19, row: 5 },
    { kind: "meetingChair", col: 16, row: 6 }, // topo oeste → olham para ESTE
    { kind: "meetingChair", col: 20, row: 6 }, // topo este → olham para OESTE
    { kind: "meetingTable", col: 17, row: 6 },
    { kind: "meetingChair", col: 16, row: 7 },
    { kind: "meetingChair", col: 20, row: 7 },
    { kind: "meetingChair", col: 17, row: 8 }, // fila sul → olham para NORTE
    { kind: "meetingChair", col: 18, row: 8 },
    { kind: "meetingChair", col: 19, row: 8 },
    { kind: "plant", col: 14, row: 9 },
    { kind: "plant", col: 22, row: 9 },
    // ── TI: QUATRO consolas de seis monitores + o posto do supervisor ──
    // Duas filas de duas consolas (rows 4 e 7), encostadas à parede norte e de
    // costas uma para a outra. O posto do SUPERVISOR fica no canto NORDESTE, o
    // único sítio onde encosta a duas paredes e não rouba largura às consolas.
    // Cada móvel serve UM lugar, na col do MEIO da sua pegada (todas ímpares de
    // propósito: assim `seatAnchor` não tem desvio nenhum para corrigir); a arte
    // sobe sobre a pegada, por isso as rows 5 e 8 ficam só com cadeiras — pôr lá
    // um móvel era escondê-lo atrás da bancada.
    //
    // A sala tem 9 cols (24-32) e a row 4 leva agora três móveis de 3 tiles, logo
    // não sobra folga nenhuma: a row 4 está cheia de parede a parede. O vão do
    // meio (col 27) que ela tinha caiu quando a mesa do supervisor passou de 2 para
    // 3 tiles — 3+1+3+3 = 10 não cabe em 9. Não custa acesso: a col 27 continua
    // livre da porta (cols 27-28, row 11) até à row 5, que é tudo o que se pisa —
    // a row 4 encosta à parede norte, ninguém a atravessa, e o vão que lá estava
    // era um nicho sem saída. A row 7, que não mexeu, guarda os 38 px de folga
    // entre consolas.
    { kind: "deskDual", col: 24, row: 4 },
    { kind: "deskDual", col: 27, row: 4 },
    { kind: "deskPC", col: 30, row: 4 },
    { kind: "officeChair", col: 25, row: 5 },
    { kind: "officeChair", col: 28, row: 5 },
    { kind: "officeChair", col: 31, row: 5 },
    { kind: "deskDual", col: 24, row: 7 },
    { kind: "deskDual", col: 28, row: 7 },
    { kind: "officeChair", col: 25, row: 8 },
    { kind: "officeChair", col: 29, row: 8 },
    // Sofá de descanso da TI, ao longo da parede da DIREITA. Esteve aqui uma
    // estante de 2 tiles (cols 31-32, row 7), retirada a pedido.
    //
    // O sofá é o de PERFIL e não o normal por uma razão de medida, não de gosto: o
    // `couch` é 3×1 e a única parede livre desta sala é a de ESTE, que corre no
    // eixo Y. Encostar-lhe um sofá de 3 tiles de LARGURA obrigava a arrancar meia
    // fila de consolas (a row 7 já leva as cols 24-26 e 28-30), e pousá-lo virado
    // a sul no meio do chão punha um sofá de sala de estar a cortar o corredor
    // entre as duas filas. De perfil, ocupa a coluna 32 e as rows 6-8 — a faixa
    // que a estante já usava, alongada — e deixa a col 31 inteira como acesso.
    //
    // A TI já não tem sofá de perfil nem bebedouro — saíram os dois a pedido. O
    // `couchSide` continua a existir como peça (arte e fatias), só não está no mapa.
    //
    // O relógio de pé fica a MEIO da parede da direita (col 32, row 8). Esteve no
    // canto sudeste (row 10), mas lá ficava escondido atrás da planta e mal se
    // chegava a ele — e agora é o relógio que bate o ponto, tem de estar à mão.
    // Chega-se-lhe por três lados (col 31, row 7 e row 9), e a col 31 continua
    // inteira como corredor.
    { kind: "grandfatherClock", col: 32, row: 8 },
    { kind: "plant", col: 31, row: 10 },
    { kind: "plantTall", col: 24, row: 10 },
    // ── Corredor: verde nas pontas e uma passadeira de boas-vindas ──
    { kind: "plantTall", col: 4, row: 12 },
    // A da ponta ESTE estava na col 32 e recuou UMA coluna. Não é gosto: a col 32
    // é a fila de dentro do vão sem porta (`EXTERIOR_OPENINGS`, col 33, rows
    // 12-14) e esta peça é sólida — deixada onde estava, selava a row 12 do vão
    // por dentro e a boca voltava a ser de duas tiles, descentrada, que foi
    // exactamente a correcção que o Kawã pediu. Uma coluna chega: o verde da ponta
    // mantém-se, agora ao lado da passagem em vez de à frente dela.
    { kind: "plantTall", col: 31, row: 12 },
    { kind: "rug", col: 16, row: 12 },
    // ── Sala de Descanso: o grupo TV-sofá-mesa alinhado no MESMO eixo ──
    // O sofá tem o encosto desenhado a norte, logo quem lá se senta olha para SUL:
    // é por isso que a mesa de café e a TV ficam ABAIXO dele, nesta ordem. Assim o
    // sofá deixa de estar "de lado para nada" e passa a fazer sala.
    //
    // O CAMINHO são as colunas 6-8, e a conta não é de olhómetro: entra-se pela
    // porta do corredor (cols 7-8, row 15) e sai-se para o relvado pelo vão sul
    // (cols 6-7, row 23). É essa faixa que fica livre de ponta a ponta — a coluna
    // 5 nunca esteve no trajecto, e é lá que encosta o canto de jogos.
    { kind: "bookshelf", col: 11, row: 16 },
    // A planta alta era o canto NORDESTE desta sala e passou para aqui, ao lado da
    // estante, porque o canto de noroeste é a ÚNICA parede desta sala onde um alvo
    // de dardos pode ficar (ver mais abaixo) e a planta cabe em qualquer lado. Não
    // desapareceu mobília nenhuma — trocou de canto, que é o mínimo que resolve.
    { kind: "plantTall", col: 12, row: 17 },
    { kind: "couch", col: 9, row: 18 },
    { kind: "rug", col: 8, row: 19 },
    { kind: "coffeeTable", col: 9, row: 20 },
    { kind: "tv", col: 9, row: 22 },
    { kind: "plant", col: 4, row: 22 },
    { kind: "plantTall", col: 12, row: 22 },
    // O canto de jogos, encostado à parede oeste: mesa ao meio e um lugar de cada
    // LADO do tampo, não os dois na mesma aresta — duas pessoas a jogar 4 em linha
    // sentam-se frente a frente. As cadeiras não precisam de dizer para onde olham:
    // `facingFor` lê-o da mesa vizinha (a `gameTable` é um `FOCUS_KIND`), por isso
    // a de cima vira-se para baixo e a de baixo para cima sozinhas.
    { kind: "gameTable", col: 4, row: 19 },
    { kind: "diningChair", col: 4, row: 18 },
    { kind: "diningChair", col: 5, row: 20 },
    // A máquina de arcade, no canto noroeste — e o sítio não teve escolha nenhuma.
    //
    // O armário sobe 38 px acima da pegada, e o que estiver na fila de cima fica
    // tapado por ele. Isso obriga-o à fila 16, a única desta sala que tem PAREDE
    // por cima (não há nada para esconder). Dessa fila, as colunas 5-10 são a
    // faixa reservada ao nome "Sala de Descanso" (`ROOM_LABELS`) e as 11-12 são a
    // estante — sobra a 4, que por sorte é o melhor sítio de qualquer maneira:
    // fecha o canto de jogos que já estava mesmo por baixo e deixa o caminho
    // (colunas 6-8, da porta do corredor ao vão sul) livre de quem está a ver.
    { kind: "arcade", col: 4, row: 16 },
    // ── Recepção: sala de espera, sem balcão ──
    // O balcão e a cadeira saíram a pedido; o sofá (sem mesinha à frente) está
    // encostado à parede leste, sobre o tapete, fora do caminho da porta.
    { kind: "plantTall", col: 14, row: 16 },
    // O canto superior direito é um relógio de ponto (era uma planta alta).
    { kind: "grandfatherClock", col: 22, row: 16 },
    // Bebedouro na parede da DIREITA (col 22, a última das 14-22) — ver a regra em
    // Vendas. Estava na col 14, que é a ESQUERDA, e ainda por cima a um tile do
    // canto do balcão. Na col 22 fica ao lado do grupo de espera, que é quem o usa.
    { kind: "waterCooler", col: 22, row: 18 },
    { kind: "rug", col: 19, row: 20 },
    { kind: "couch", col: 20, row: 20 },
    // A escada para o 1.º andar, encostada à parede oeste. A planta que estava no
    // canto (14,22) saiu: era o degrau de baixo.
    { kind: "stairs", col: 14, row: 19 },
    { kind: "plant", col: 22, row: 22 },
    // ── Copa: cozinha na fila 18, mesa com seis lugares por baixo dela ──
    //
    // A bancada e o frigorífico estiveram na row 22 — a última fila da sala, com a
    // parede sul logo por baixo. Mas as duas peças desenham-se de FRENTE (portas,
    // puxadores, lava-loiça no tampo) e assentam pelo chão, portanto a frente
    // olhava para baixo: para a parede. Encostados assim ficavam de costas para a
    // sala, a servir ninguém. Sobem quatro filas e passam a olhar para dentro,
    // para a mesa — que é o que uma copa é.
    //
    // A col 28 fica de fora da bancada de propósito: é a coluna da porta (`DOORS`
    // tem a Copa ↔ corredor em [28,15]), e quem entra desce por ela até à mesa sem
    // contornar nada. O frigorífico fica do outro lado dessa passagem.
    //
    // As duas peças TROCARAM de lado a pedido — o frigorífico à esquerda, a
    // bancada à direita. Não é uma troca de coordenadas: a bancada ocupa 4 tiles e
    // o frigorífico 1, por isso pôr um no `col` do outro deixava a bancada a
    // transbordar três tiles para lá da parede. Cada um encosta ao topo da SUA
    // parede — o frigorífico na primeira coluna da sala (24), a bancada nas
    // últimas quatro (29-32) — e a coluna da porta continua livre, que era a única
    // coisa que a disposição antiga tinha de mesmo intocável.
    { kind: "plantTall", col: 24, row: 16 },
    // Relógio de ponto no canto superior direito, no lugar da planta alta.
    { kind: "grandfatherClock", col: 32, row: 16 },
    { kind: "fridge", col: 24, row: 18 },
    { kind: "counter", col: 29, row: 18 }, // cols 29-32
    // O bebedouro e a planta ficam onde estavam, na col 32: a bancada acaba
    // exactamente em cima do bebedouro (row 18 contra row 20) e a coluna da
    // direita continua espaçada de cima a baixo — planta alta (16), o topo da
    // bancada (18), bebedouro (20), planta (22).
    { kind: "waterCooler", col: 32, row: 20 },
    { kind: "plant", col: 32, row: 22 },
    // A mesa encosta ao canto ESQUERDO: cols 25-26, com a cadeira da esquerda na
    // col 24, a primeira da sala — mais para a esquerda já era parede. Deixa o
    // meio da Copa (cols 28-31) aberto, que é por onde se anda da porta [28,15]
    // até à saída sul e à cozinha.
    { kind: "diningChair", col: 25, row: 19 },
    { kind: "diningChair", col: 26, row: 19 },
    { kind: "diningTable", col: 25, row: 20 }, // cols 25-26, rows 20-21
    { kind: "diningChair", col: 24, row: 20 },
    { kind: "diningChair", col: 27, row: 20 },
    { kind: "diningChair", col: 25, row: 22 },
    { kind: "diningChair", col: 26, row: 22 },
    // ── Lago do jardim ────────────────────────────────────────────
    // A casa assenta na metade NORTE do lago (rows 27-29 de 26-33), não ao centro:
    // deixa a água toda do lado de baixo à vista, que é o lado por onde se passa a
    // pé, e é aí que ela se lê como casa sobre água em vez de casa num descampado.
    // Meio tile à direita do eixo (o centro do lago cai em 16,5 e uma pegada par só
    // consegue 16 ou 17) — o desencontro ajuda: centrada ao milímetro parecia posta
    // a régua. A elipse acolhe-a inteira: o canto mais afastado, (19,5 · 27,5), dá
    // 0,51 na equação, bem dentro de 1.
    { kind: "floatingHouse", col: 14, row: 27 },
    // ── As duas casas que sobram, no relvado a nascente do lago ────
    // As quatro novas repartem-se 2+2: as duas cápsulas vão render as do canto oeste
    // do parque (mais abaixo, nos `PROPS` do recinto) e estas duas ficam no descampado
    // que havia deste lado — a cúpula e o modular de madeira, que são as duas que se
    // lêem como peças de EXPOSIÇÃO e não como unidades estacionadas.
    //
    // O relvado livre são as cols 26-42 × rows 24-34: a poente fecha-o a perna
    // norte-sul do caminho de pedra (cols 24-25), a norte a parede sul do edifício
    // (row 23), a sul a cerca (row 35) e a nascente a moldura do mundo (a col 43 é
    // borda, não relva). Duas casas em dezassete tiles é RALO de propósito: o pedido
    // era encher um vazio, não trocá-lo por um beco entre casas.
    //
    // As rows 24-26 ficam livres de ponta a ponta porque a porta das traseiras da
    // Copa está em [30,23] e [31,23]: uma casa a começar na row 24 selava-a por
    // dentro — o vão continuava aberto e dava para uma parede, que é o pior dos dois
    // mundos (nada acusa, e só quem lá vai a pé descobre). Daí a linha de chão em 30,
    // que deixa três filas de aproximação em cima e quatro de recuo até à cerca.
    //
    // Entre as duas ficam as cols 32-34 abertas, e não é só folga: é por onde se
    // desce da porta da Copa ao portão do parque sem contornar nada. A cúpula fica a
    // poente porque transborda 56 px (1,75 tiles) acima da pegada — encostada ao
    // corredor do edifício esse transbordo cai onde se PASSA, não onde se pára.
    //
    // As árvores do relvado FICAM onde estão e não foi esquecimento: elas ordenam-se
    // por `py` e as casas por `(row + h) * TILE`, portanto as que estão abaixo da
    // linha de chão desenham-se À FRENTE (uma árvore em frente a uma casa é o que se
    // quer) e as que estão acima ficam atrás, a assomar por cima do telhado.
    { kind: "homeDome", col: 27, row: 27 },
    { kind: "homeModular", col: 35, row: 27 },
    // ── Parque de mobile homes (exterior sul) ────────────────────
    // A cerca de madeira veda o recinto a NORTE, com um PORTÃO ao centro (cols 19-24
    // livres) alinhado com o corredor que desce do jardim: é por aqui que se entra.
    //
    // A fila vem de `PARK_ROW` e não de um `35` escrito oito vezes: é a MESMA linha
    // que o `placeName` usa para saber que daqui para baixo se está no pátio. Com
    // dois números soltos, mover a cerca deixava o rótulo do sítio a trocar de nome
    // no meio do relvado — e nada nem ninguém dava por isso.
    { kind: "fence", col: 3, row: PARK_ROW },
    { kind: "fence", col: 7, row: PARK_ROW },
    { kind: "fence", col: 11, row: PARK_ROW },
    { kind: "fence", col: 15, row: PARK_ROW },
    { kind: "fence", col: 25, row: PARK_ROW },
    { kind: "fence", col: 29, row: PARK_ROW },
    { kind: "fence", col: 33, row: PARK_ROW },
    { kind: "fence", col: 37, row: PARK_ROW },
    // Muro baixo de pedra com sebe fecha o recinto a SUL (junto à borda do mundo).
    { kind: "stoneWall", col: 3, row: 50 },
    { kind: "stoneWall", col: 7, row: 50 },
    { kind: "stoneWall", col: 11, row: 50 },
    { kind: "stoneWall", col: 15, row: 50 },
    { kind: "stoneWall", col: 19, row: 50 },
    { kind: "stoneWall", col: 23, row: 50 },
    { kind: "stoneWall", col: 27, row: 50 },
    { kind: "stoneWall", col: 31, row: 50 },
    { kind: "stoneWall", col: 35, row: 50 },
    { kind: "stoneWall", col: 39, row: 50 },
    // As casas do parque: duas em cima, duas em baixo, a ladear o estacionamento,
    // cada uma virada PARA DENTRO — a fachada olha para o asfalto, que é por onde
    // se passa. Eram quatro cópias do MESMO modelo, depois três modelos em quatro
    // lugares; agora são QUATRO modelos distintos e nada se repete, porque as duas
    // do canto OESTE — que é o canto que se pediu para trocar — deixaram de ser
    // caravanas: a bege de 3/4 deu lugar à cápsula de portas fechadas
    // (`homeCapsuleClosed`) e o alçado verde comprido à cápsula elevada branca
    // (`homeCapsule`). As duas de leste ficam exactamente como estavam.
    //
    // ATENÇÃO ao sufixo `Flip`: ele NÃO quer dizer «virado para dentro», quer dizer
    // «espelhado em relação ao ficheiro». E os ficheiros não vêm todos virados para
    // o mesmo lado — a `v1` e a `v2` traziam a porta à DIREITA, a `v3` (o arco) traz
    // a porta à ESQUERDA. Ler o sufixo como orientação foi exactamente o que pôs a
    // casa de arco de costas para o parque, a mostrar a traseira a quem entra e a
    // fachada às árvores. A regra é a ORIENTAÇÃO, e sai por ficheiro: a leste quer-se
    // a porta à esquerda (`mobileHomeSideFlip` espelhada, `mobileHomeArch` NÃO). A
    // oeste o problema deixou de existir — as duas cápsulas já vêm do render com a
    // fachada e a porta viradas para a frente-direita, ou seja para o asfalto, e por
    // isso entram SEM espelho nenhum. Restam dois `Flip` no recinto, ambos a leste.
    //
    // As linhas não são as mesmas nos quatro porque o que tem de alinhar é o
    // CHÃO, não o topo: o desenho assenta em `(row + h) * TILE`, logo uma casa de
    // 3 tiles de fundo tem de começar uma linha ABAIXO de uma de 4 para pousar no
    // mesmo sítio. Daí 38+3 = 37+4 = linha 41 no par de cima e 45+3 = 44+4 =
    // linha 48 no de baixo. Entre os dois pares ficam 4 linhas de asfalto livres.
    // A de cima subiu de fundo 4 para 3 com a troca, e por isso a linha de partida
    // desceu de 37 para 38: o que se conserva é a linha de CHÃO, nunca o `row`.
    //
    // As colunas a leste mudaram com o crescimento para 7 e 9 tiles: 32 (e não 34)
    // porque 9 tiles a partir de 34 acabavam na coluna 42 e engoliam a árvore grande
    // do canto; a de arco recuou de 37 para 35 pela mesma razão, com a sebe que lá
    // estava a fugir 1,4 tiles para fora. A oeste ambas ficam em 3, encostadas à
    // cerca, que é onde já estavam — as cápsulas são mais curtas do que o alçado que
    // saiu (6 e 5 tiles contra 9), o que devolve relva entre a casa de baixo e o
    // candeeiro da coluna 12 em vez de a apertar.
    { kind: "homeCapsuleClosed", col: 3, row: 38 },
    { kind: "mobileHomeSideFlip", col: 32, row: 38 },
    { kind: "homeCapsule", col: 3, row: 45 },
    { kind: "mobileHomeArch", col: 35, row: 44 },
    // A placa grande com o logótipo Green Village Mobile Homes, logo à entrada.
    { kind: "parkSign", col: 25, row: 37 },
    // Candeeiros de rua pretos com luz quente, nos quatro cantos do asfalto.
    //
    // Os dois do lado oeste estão na coluna 12 e não na 11 porque o alçado lateral
    // de baixo passou a ocupar 9 colunas (3-11): na 11 o candeeiro e a casa eram
    // ambos sólidos no mesmo tile. A 12 é também a PRIMEIRA coluna de asfalto —
    // com a 31 a ser a última, os quatro candeeiros passam a marcar os cantos da
    // laje em vez de ficarem um tile fora dela.
    { kind: "streetLamp", col: 12, row: 40 },
    { kind: "streetLamp", col: 31, row: 40 },
    { kind: "streetLamp", col: 12, row: 47 },
    { kind: "streetLamp", col: 31, row: 47 },
    // Aqui estiveram duas carrinhas (pickup), desenhadas em código, primeiro
    // dentro dos lugares pintados e depois atrás deles na linha 45. Saíram — e a
    // razão fica escrita porque é uma decisão de LEITURA, não uma limpeza: com os
    // quatro carros já estacionados nas baias, as carrinhas ficavam soltas na
    // faixa de baixo, fora de qualquer lugar, e um parque com veículos fora dos
    // lugares lê-se como veículos mal estacionados, não como parque cheio. A
    // faixa 45-47 é agora o corredor de saída, que é o que uma faixa de asfalto
    // vazia à frente dos lugares deve ser. Voltar a pôr um veículo aqui exige
    // primeiro decidir o que ele está a fazer fora de um lugar.
    //
    // Linhas brancas dos lugares, pintadas por cima do asfalto (não bloqueiam).
    //
    // O bloco nascente veio com elas (24→23), e não por arrasto: na 24 ocupava as
    // cols 24-27 e a carrinha vermelha passava a pousar em cima da última linha —
    // um carro a tapar meio lugar pintado lê-se como mal estacionado, não como
    // estacionado. O tile ganho fecha ainda uma assimetria antiga: os dois blocos
    // estavam centrados em x=720 e o eixo do recinto é o do vão do portão, x=704
    // (cols 19-24 livres na vedação).
    //
    // Cada bloco passou a valer TRÊS baias em vez de duas (6×4, riscas de 2 em 2
    // tiles) e subiu da linha 42 para a 41, que é onde um carro de 4 tiles tem de
    // começar para o seu chão cair na linha 45 — a mesma em que caem as casas.
    // É a lei de alinhamento do parque: as peças alinham pelo CHÃO,
    // `row + h`, e não pelo topo. Com 15-20 e 23-28 os centros são 576 e 832,
    // ±128 do eixo, e o vão livre entre eles (21-22) é a passagem do portão até
    // ao asfalto: quem entra desce a direito sem atravessar lugar nenhum.
    { kind: "parkingLines", col: 15, row: 41 },
    { kind: "parkingLines", col: 23, row: 41 },
    // Os quatro carros, um por baia das pontas de cada bloco.
    //
    // As colunas não são escolha de gosto, são as únicas que fecham as três
    // contas ao mesmo tempo: caem no centro exacto de uma baia pintada (o carro
    // tem 2 tiles, a baia tem 2), deixam o intervalo de 1 tile que a fila exige
    // entre sólidos, e ficam espelhadas no eixo do portão — 512/896 e 640/800,
    // ±192 e ±96 de 704. As duas baias que sobram são as do meio de cada bloco
    // (17-18 e 25-26), também elas espelhadas: um parque com quatro carros e
    // dois lugares vagos lê-se ocupado, um parque cheio lê-se cenário.
    //
    // Os modelos alternam de silhueta em vez de ficarem agrupados por tipo, para
    // que a fila não leia como um stand: desportivo, sedan, SUV, super.
    { kind: "carSport", col: 15, row: 41 },
    { kind: "carSedan", col: 19, row: 41 },
    { kind: "carSuv", col: 23, row: 41 },
    { kind: "carSuper", col: 27, row: 41 },
    // Contentor verde, caixotes metálicos, cones e floreiras — o recheio do recinto.
    //
    // O lixo andou para leste: o contentor estava na coluna 9 e o alçado lateral
    // de cima ocupa as colunas 3-11, ou seja o contentor estava debaixo da casa,
    // encostado à parede dela. Agora começa na 14, com as colunas 12 e 13 vazias a
    // servir de intervalo, e os dois caixotes seguem-no (17 e 18) — em vez de
    // ficarem sozinhos do outro lado do contentor, formam com ele um recanto de
    // lixo entre a casa e os cones (21 e 23), que é o que se vê num parque a
    // sério. Do lado leste sobra folga de sobra: a casa desse lado só começa na 32.
    { kind: "dumpster", col: 14, row: 48 },
    { kind: "trashCan", col: 17, row: 48 },
    { kind: "trashCan", col: 18, row: 48 },
    { kind: "cone", col: 21, row: 48 },
    { kind: "cone", col: 23, row: 48 },
    // Os canteiros são os ÚNICOS objectos do recinto que não têm função — por isso
    // são os únicos que precisam de justificar a posição. Estavam três, em cols
    // 8/16/34 e linhas 40/39/47: nenhum eixo, nenhum par, cada um a olhar para
    // sítio nenhum. Ficam DOIS, e são os que ladeiam o vão do portão: com a pegada
    // de 2 tiles ocupam as cols 17-18 e 25-26, isto é, ENCOSTAM às duas ombreiras
    // do vão (cols 19-24 livres na vedação) em vez de ficarem a um tile delas.
    // Centros em 576 e 832, ±128 do eixo x = 704. Deixam de ser decoração
    // espalhada e passam a ser o que um parque real tem: canteiros a marcar por
    // onde se entra.
    //
    // O par de baixo, que fechava os topos da fila de estacionamento nas cols 12 e
    // 30, SAIU. Estava lá para dar remate a uma fila que na altura tinha duas
    // carrinhas soltas no meio de muito asfalto vazio; com as quatro baias
    // pintadas ocupadas, o remate já é dado pelas próprias riscas e os canteiros
    // passaram a ler-se como mato a crescer encostado aos carros. Aquele tile
    // também não estava de graça: era o que obrigava a fila a caber em 16 colunas
    // em vez de 20.
    { kind: "flowerBox", col: 17, row: 39 },
    { kind: "flowerBox", col: 25, row: 39 },
    // ── A escotilha, no canto NASCENTE-NORTE do jardim ──────────
    // A boca do bunker, onde o Kawã a quis: «vamos deixar o bunker aqui em cima no
    // canto direito». Cols 40-42, rows 3-4 — à altura do topo do prédio, encostada
    // ao relvado do canto.
    //
    // A coluna é escolhida, não arredondada: a copa da árvore grande da col 38
    // chega aos 1256 px e a tampa começa nos 1280, portanto não há um único pixel
    // de folhagem por cima do aço (a árvore desenha-se DEPOIS, pela ordem em y, e
    // um sobreposto de oito pixels via-se). Por cima fica o arbusto da col 41, que
    // acaba 45 px acima da tampa, e por baixo a árvore da col 40,5 só começa na
    // row 12. Um claro de relva limpo, que é o que uma tampa que abre precisa.
    //
    // Esse claro foi DEPOIS estreitado de propósito: «coloque umas moitas no
    // bunker, só pra deixar ele um pouco escondido». Seis arbustos (`MOITAS_BUNKER`,
    // em `OfficeSpace.tsx`) mordem o betão à volta para lhe partir a silhueta, mas
    // o VÃO — `HATCH_GAP`, por onde as folhas de aço deslizam — continua a 100% à
    // vista, e é essa a parte do claro que a tampa precisava mesmo. As moitas são
    // cenário puro: não passam por aqui, não têm pegada e não criam colisão.
    //
    // NÃO é sólida — pisa-se de propósito, é isso que a põe ao alcance do E.
    { kind: "bunkerHatch", col: 40, row: 3 },
    // ── Subsolo: a sala das automações ──────────────────────────
    // A leitura da sala é uma nave: duas paredes de bastidores a piscar, um
    // corredor de cada lado, e a máquina sozinha no eixo, de frente para a porta.
    //
    // Os dois ecrãs de parede ficam na PRIMEIRA linha da sala (row 56): a arte sobe
    // 48 px acima da pegada, portanto encosta ao betão da parede norte em vez de
    // pairar — é o mesmo truque da TV da Sala de Descanso, e por isso também não
    // são sólidos (estão pendurados, não se esbarra neles).
    { kind: "analyticsScreen", col: 15, row: 56 },
    { kind: "analyticsScreen", col: 27, row: 56 },
    // Doze bastidores, seis de cada lado, em duas filas por parede. Cada um ocupa
    // UM tile de chão e sobe o dobro — a mesma pegada-mais-alta-que-larga da
    // `plantTall`. As filas param nas cols 17 e 27, deixando dois corredores
    // norte-sul livres (cols 18-19 e 25-26) para se andar à volta da máquina.
    { kind: "serverRack", col: 15, row: 58 },
    { kind: "serverRack", col: 16, row: 58 },
    { kind: "serverRack", col: 17, row: 58 },
    { kind: "serverRack", col: 27, row: 58 },
    { kind: "serverRack", col: 28, row: 58 },
    { kind: "serverRack", col: 29, row: 58 },
    { kind: "serverRack", col: 15, row: 61 },
    { kind: "serverRack", col: 16, row: 61 },
    { kind: "serverRack", col: 17, row: 61 },
    { kind: "serverRack", col: 27, row: 61 },
    { kind: "serverRack", col: 28, row: 61 },
    { kind: "serverRack", col: 29, row: 61 },
    // A máquina do meio. Cols 20-24 numa sala de 15 de largo (cols 15-29): cinco
    // tiles de margem de cada lado, exactamente. É por isto que a sala tem largura
    // ÍMPAR — só assim o eixo dela cai no MEIO de uma coluna, que é onde uma peça
    // de 5 tiles consegue assentar centrada.
    { kind: "aiConsole", col: 20, row: 59 },
    // ── 1.º andar — antessala (rows 82-88, cols 14-22) ──────────
    // O poço da escada por cima dos degraus da Recepção, a secretária de quem
    // atende o CEO, e um sofá para quem espera. A linha entre o patamar e a porta
    // do gabinete (cols 14-18, rows 82-84) fica livre.
    { kind: "stairwell", col: 14, row: 85 },
    { kind: "plantTall", col: 22, row: 82 },
    { kind: "receptionDesk", col: 19, row: 84 },
    // Atrás do balcão, virada para quem chega (para baixo) — a direcção sai do
    // balcão, que fica por baixo dela. É a cadeira giratória verde do pacote da
    // reunião porque é a única de escritório com a vista de FRENTE desenhada (a
    // `officeChair` só tem a de costas).
    { kind: "meetingChair", col: 20, row: 83 },
    { kind: "waterCooler", col: 22, row: 84 },
    { kind: "couchSide", col: 22, row: 86 },
    { kind: "plant", col: 18, row: 88 },
    // ── 1.º andar — gabinete do CEO (rows 70-80, cols 14-22) ─────
    // As janelas da fachada norte (cols 16, 18, 20) ficam à vista entre as duas
    // estantes. A secretária está no eixo da sala, com a cadeira virada para ela
    // como na TI; em baixo, dois recantos de sofá. A faixa do rótulo (row 80,
    // cols 15-20) e a coluna da porta até à secretária ficam livres.
    { kind: "bookshelf", col: 14, row: 70 },
    { kind: "bookshelf", col: 21, row: 70 },
    { kind: "deskPC", col: 17, row: 72 },
    { kind: "officeChair", col: 18, row: 73 },
    { kind: "plantTall", col: 14, row: 72 },
    { kind: "waterCooler", col: 22, row: 73 },
    { kind: "rug", col: 14, row: 75 },
    { kind: "couch", col: 14, row: 76 },
    { kind: "coffeeTable", col: 14, row: 77 },
    { kind: "couchSide", col: 22, row: 75 },
    { kind: "plant", col: 14, row: 79 },
    { kind: "plant", col: 22, row: 79 },
];
/**
 * As árvores do jardim — e, desde *"o tronco das árvores deve ter física, não é
 * possível o personagem passar por dentro"*, também a colisão delas.
 *
 * Viveram sempre no cliente, numa lista chamada `scenery` com o comentário
 * "posições puramente visuais". Já não é verdade: um tronco é um obstáculo, e
 * neste mundo obstáculo escreve-se na grelha do `buildWalls`, que é partilhada.
 * Tinha de subir para aqui inteiro — deixar o desenho lá e a colisão cá eram
 * duas listas a jurar a mesma coisa, e a que se esquece é sempre a outra.
 *
 * **Porquê uma lista à parte e não `PropKind`s novos:** um prop ancora no canto
 * superior esquerdo, mede tiles inteiros (`w`×`h`) e vive em coordenadas
 * inteiras. Uma árvore ancora no PÉ, ao centro (`setOrigin(0.5, 1)`), e as
 * posições são fraccionárias de propósito — 35,6 · 32,8 · 27,1 são o que a põe à
 * frente e abaixo do pé de uma casa em vez de dentro do telhado dela (ver os
 * comentários por baixo). Metê-las em `PROPS` era arredondá-las a tile inteiro,
 * isto é, mexer no jardim para arrumar a gaveta. A geometria é outra; a lista
 * também.
 *
 * O que fica a bloquear é o TRONCO e só ele — um tile, o do `trunkTile`. A copa
 * continua a atravessar-se, e é assim que tem de ser: uma copa de árvore grande
 * são 80px de folhagem desenhada POR CIMA de quem passa, e a bloquear vedava
 * metade do relvado por causa de sombra.
 */
export const TREES = [
    // Faixa de relva ESQUERDA (cols 0-2) — coníferas de vários tamanhos.
    { size: "big", col: 1.6, row: 8 },
    { size: "medium", col: 1.5, row: 14 },
    { size: "small", col: 2.0, row: 18 },
    { size: "medium", col: 1.5, row: 24 },
    { size: "small", col: 1.9, row: 30 },
    // Nenhuma destas cinco fecha a faixa: a relva a poente tem DUAS colunas
    // pisáveis (a 0 é sebe de borda) e os troncos caem alternadamente na 1 e na 2,
    // nunca na mesma linha. Quem desce pela esquerda contorna-as sempre.
    // Faixa de relva DIREITA (cols 34-42) — mais árvores, mundo mais verde.
    { size: "big", col: 38.0, row: 7 },
    { size: "medium", col: 40.5, row: 12 },
    { size: "small", col: 36.0, row: 10 },
    { size: "medium", col: 39.0, row: 18 },
    { size: "big", col: 37.0, row: 24 },
    // Estas duas emolduram a `homeModular` (cols 35-40, pé na linha 30) em vez de a
    // atravessarem. Estavam DENTRO dela: a conífera pequena nascia-lhe do telhado. A
    // causa é de ordenação — a vegetação desenha em `row × 32` e entra na cena
    // DEPOIS dos props, portanto com profundidade igual OU maior ganha sempre a
    // planta. A régua para não repetir: à frente de uma casa, uma árvore fica abaixo
    // do pé dela (`row + h`); ao lado, fora das colunas da pegada.
    { size: "medium", col: 42.0, row: 28 }, // nascente, encostada ao muro
    { size: "small", col: 35.6, row: 32.8 }, // sudoeste, à frente e abaixo do pé
    // RELVADO SUL (rows 24-32) — anilha à volta do lago (a água ocupa o meio, cols
    // ~11-22, rows ~26-31). Norte, oeste e este; a sul é tudo água.
    { size: "small", col: 5.0, row: 25 }, // NW do lago
    { size: "medium", col: 4.5, row: 30 }, // OESTE do lago
    // O corredor entre a água (acaba na col 23,5) e a cúpula (começa na 27) é do
    // CAMINHO, não da vegetação: o `GARDEN_PATH` desce por ali nas colunas 24-25 até
    // à linha 34. Houve aqui peças plantadas antes de o carreiro existir, com o
    // tronco em cima das lajes — uma árvore a nascer do meio da pedra, a única coisa
    // do jardim que se lia logo como erro. Regra que fica, e que agora é um teste:
    // nada com tronco nas colunas 24-25 entre as linhas 24 e 34.
    // Margem NORTE do lago, mas a poente da água. Estava na col 13: o tronco
    // fechava a única fila de relva (a 25) entre a parede da Recepção e a água, e
    // como se desenha quase todo na fila de cima lia-se como parede invisível.
    { size: "small", col: 9.5, row: 25.3 },
    // A coroa da `homeDome` (cols 27-30, pé na linha 30). Esta vai ABAIXO do pé da
    // casa, portanto à frente dela pela ordenação em y — que é o que se quer: antes
    // disto estava literalmente dentro da cúpula, profundidade 880 contra os 960 da
    // casa, escondida atrás dela com o bico de fora.
    { size: "medium", col: 27.1, row: 32.6 }, // relva a sul da cúpula, fora das lajes
    { size: "small", col: 33.2, row: 28 }, // na relva entre a cúpula e o modular
    // PARQUE DE MOBILE HOMES (sul, rows 35-51) — coníferas altas a emoldurar os 4
    // cantos do lote. Em relva, a oeste (cols<3) e este (cols>40) das casas, entre a
    // cerca (row 35) e o muro de pedra (row 50).
    { size: "big", col: 1.6, row: 39 }, // canto NO do estacionamento
    { size: "medium", col: 1.7, row: 49 }, // canto SO (a norte do muro)
    { size: "big", col: 41.2, row: 39 }, // canto NE
    { size: "medium", col: 41.1, row: 49 }, // canto SE
];
/**
 * O tile que o tronco ocupa — o único que a árvore bloqueia.
 *
 * A árvore desenha-se com o pé no ponto `(col × 32, row × 32)` e a arte a subir
 * dali (`setOrigin(0.5, 1)`). Esse ponto cai numa LINHA entre dois tiles quando a
 * linha é inteira, e o tronco fica no de CIMA — que é a mesma convenção da
 * mobília, onde uma peça de altura 1 na linha R desenha o pé em `(R+1) × 32` e
 * ocupa a linha R. Daí o `ceil(row) - 1`: com 8 dá 7, com 25,3 dá 25 (a linha
 * onde o pé de facto está).
 *
 * A coluna é a que contém o tronco, e aqui a grelha tem um limite honesto: com
 * uma coluna inteira (2,0 · 5,0 · 38,0) o eixo do tronco cai EM CIMA da fronteira
 * e uns 6px de casca ficam do lado de lá, num tile que continua pisável. Não se
 * atravessa na mesma — para o corpo entrar no tile bloqueado ainda tem de vencer
 * os 10px de `COLLIDE_MARGIN`, que são mais do que a casca que sobra. Bloquear os
 * DOIS tiles resolvia 6px de arte com 32px de parede invisível: pior a emenda.
 */
export function trunkTile(tree) {
    return { col: Math.floor(tree.col), row: Math.ceil(tree.row) - 1 };
}
/**
 * Ponto de spawn em pixels — na Recepção, um passo dentro da porta e ao lado do
 * balcão. Fica de fora da faixa do rótulo e do caminho de quem entra: aparece-se
 * no sítio onde uma pessoa realmente pararia, não em cima da mobília.
 */
export const SPAWN = { x: (20 + 0.5) * TILE, y: (17 + 0.5) * TILE };
// ── Grelhas derivadas (construídas uma vez) ─────────────────
function inRect(col, row, r) {
    return col >= r.col && col < r.col + r.w && row >= r.row && row < r.row + r.h;
}
/** Grelha de piso: o tipo de chão de cada tile, ou `null` se for parede. */
function buildFloors() {
    const grid = [];
    for (let row = 0; row < ROWS; row++) {
        const line = [];
        for (let col = 0; col < COLS; col++) {
            let floor = null;
            for (const r of ROOMS) {
                if (inRect(col, row, r)) {
                    floor = r.floor;
                    break;
                }
            }
            line.push(floor);
        }
        grid.push(line);
    }
    // As portas abrem a divisória — pisáveis, com o piso do corredor. No subsolo o
    // "corredor" é rocha: um vão de mármore de lobby no meio de um bunker de betão
    // dava um quadrado claro a gritar no chão.
    for (const [col, row] of DOORS) {
        const line = grid[row];
        if (line)
            line[col] = isUnderground(row) ? "concrete" : isUpstairs(row) ? "wood" : "lobby";
    }
    return grid;
}
const FLOORS = buildFloors();
function buildWalls() {
    const walls = [];
    for (let row = 0; row < ROWS; row++) {
        const line = [];
        for (let col = 0; col < COLS; col++) {
            // Parede = tudo o que não é chão de nenhuma sala nem porta.
            line.push(FLOORS[row]?.[col] == null);
        }
        walls.push(line);
    }
    // ── Jardim pisável: o exterior deixa de ser "prisão". A cintura de relva à volta
    // do prédio passa a ser CHÃO (o avatar sai mesmo lá para fora), EXCEPTO a moldura-
    // borda do mapa (uma sebe que veda o mundo) e o lago (a água bloqueia). Assim o
    // exterior ganha vida navegável sem o servidor ter de distinguir relva de água —
    // é tudo a mesma grelha de colisão.
    //
    // ORDEM: este passo corre ANTES da mobília sólida. Antes fazia-se ao contrário e
    // este loop RESET-ava para pisável qualquer prop sólido colocado no exterior — o
    // que tornaria a cerca, as caravanas e os carros do parque atravessáveis. Ao
    // aplicar a mobília sólida DEPOIS do exterior, um prop no parque bloqueia mesmo.
    for (let row = 0; row < ROWS; row++) {
        for (let col = 0; col < COLS; col++) {
            if (!isOutside(col, row))
                continue;
            // A sebe é a moldura da SUPERFÍCIE (rows 0-51), não da grelha inteira. Se
            // isto lesse `ROWS - 1` a linha de baixo do jardim (row 51) deixava de ser
            // borda — abria-se um rasgo na sebe sul e saía-se do mundo pelo fundo.
            const border = row === 0 || col === 0 || row === SURFACE_ROWS - 1 || col === COLS - 1;
            const line = walls[row];
            if (line)
                line[col] = border || inLake(col, row);
        }
    }
    // A mobília sólida ocupa a sua pegada e passa a bloquear — por ÚLTIMO, para valer
    // tanto no interior como no parque exterior (ver nota de ordem acima).
    for (const prop of PROPS) {
        const spec = PROP_SPECS[prop.kind];
        if (!spec.solid)
            continue;
        for (let dy = 0; dy < spec.h; dy++) {
            // Sem `recorte` a pegada bloqueia inteira; com ele, só os tiles marcados.
            // Um `.` NÃO desbloqueia o que já estava bloqueado (parede do prédio, sebe,
            // lago, outra peça) — o recorte diz o que ESTA peça ocupa, não o que liberta.
            const fila = spec.recorte?.[dy];
            for (let dx = 0; dx < spec.w; dx++) {
                if (fila && fila[dx] !== "#")
                    continue;
                const line = walls[prop.row + dy];
                if (line && prop.col + dx >= 0 && prop.col + dx < COLS)
                    line[prop.col + dx] = true;
            }
        }
    }
    // E os troncos, pela mesma razão e no mesmo sítio da ordem: o passo do jardim
    // corre ANTES e voltaria a abrir cada um deles. Um tile por árvore — o do pé —,
    // e a copa fica de fora (ver `TREES`). Um tronco em cima de água ou de uma peça
    // sólida não muda nada: já lá estava bloqueado, e `true` sobre `true` é `true`.
    for (const tree of TREES) {
        const { col, row } = trunkTile(tree);
        const line = walls[row];
        if (line && col >= 0 && col < COLS)
            line[col] = true;
    }
    // As portas exteriores furam a parede-perímetro do prédio → vãos pisáveis. E as
    // aberturas sem porta furam-na exactamente da mesma maneira: para a colisão, um
    // vão é um vão — a porta só existe do lado do desenho.
    //
    // No FIM de propósito, como as portas: o passo do jardim e o da mobília sólida
    // correm antes e voltariam a fechar isto (o bebedouro está na col 32, à beira).
    for (const [col, row] of [...EXTERIOR_DOORS, ...EXTERIOR_OPENINGS]) {
        const line = walls[row];
        if (line)
            line[col] = false;
    }
    return walls;
}
/** Grelha de colisão: `WALLS[row][col] === true` ⇒ intransponível. */
export const WALLS = buildWalls();
/** Tipo de piso num tile (para o cliente pintar o chão). `null` = parede/fora. */
export function floorAt(col, row) {
    if (row < 0 || row >= ROWS || col < 0 || col >= COLS)
        return null;
    return FLOORS[row]?.[col] ?? null;
}
/**
 * Em que sala cai um ponto em pixels? É o gatilho do áudio por sala: quando o
 * jogador local passa de uma sala para outra, a app entra na call do cargo dessa
 * sala (TI, Vendas, Reunião…). O corredor e as portas devolvem `null` — zona de
 * passagem, sem call. Só conta o rectângulo de chão da sala (a divisória é parede).
 */
export function roomAt(px, py) {
    if (px < 0 || py < 0 || px >= MAP_WIDTH || py >= MAP_HEIGHT)
        return null;
    const col = Math.floor(px / TILE);
    const row = Math.floor(py / TILE);
    for (const r of ROOMS) {
        if (r.name !== "" && inRect(col, row, r))
            return r;
    }
    return null;
}
/**
 * O tile está FORA do prédio (na cintura de jardim)? O cliente usa isto para
 * pintar relva/árvores/lago em vez de parede cinzenta, e para NÃO escurecer o
 * exterior com a luz ambiente interior. Não afecta colisão: fora do prédio não é
 * chão de sala nenhuma, logo já é `isWall`/`!isWalkable` — o jardim é cenário.
 */
export function isOutside(col, row) {
    // O subsolo não é "fora do prédio" — é DEBAIXO dele. Sem esta guarda, o bunker
    // caía no ramo do jardim: relva por cima da rocha, e o passo do exterior em
    // `buildWalls` abria a faixa subterrânea inteira a pisável.
    //
    // O 1.º andar, pela mesma razão: o jardim à volta dele é o de BAIXO (o cliente
    // mostra-o por outra câmara). Lá em cima, fora das salas, é telhado ou ar.
    if (row >= SURFACE_ROWS)
        return false;
    return (col < BUILDING.col || col >= BUILDING.col + BUILDING.w || row < BUILDING.row || row >= BUILDING.row + BUILDING.h);
}
/**
 * Este tile de parede pertence a um tramo VERTICAL — dos que se desenham finos?
 *
 * *"todas as paredes do office 2d que estão na vertical, vamos deixar elas mais
 * finas, pois estão muito grossas… as horizontais ficaram do mesmo jeito"*. Vista
 * de cima, uma parede vertical enchia o tile de lado a lado: 32px de betão a
 * cortar o escritório. Uma divisória a sério tem metade — mas SÓ a vertical, e é
 * esta função que decide quais são, para o cliente não ter de adivinhar.
 *
 * Um tramo corre de cima para baixo quando tem parede ACIMA **e** ABAIXO. Um
 * tramo horizontal falha logo aqui (tem chão ou jardim de um dos lados), e por
 * isso fica exactamente como estava — que é metade do pedido.
 *
 * E ainda tem de haver espaço ABERTO de pelo menos um lado, senão o tile está no
 * meio de um bloco maciço de parede, onde não há espessura nenhuma para se ver:
 * uma capa fina lá dentro só desenhava uma risca escura no meio da pedra.
 *
 * "Aberto" é chão pisável OU jardim. O jardim tem de contar, senão as paredes do
 * perímetro — relva de um lado, sala do outro — não se liam como divisórias e o
 * prédio ficava com a cintura grossa que o pedido manda tirar.
 *
 * Só decide DESENHO. A colisão continua a ser o tile inteiro, como em qualquer
 * grelha: a pedra fina fica ao meio e o que sobra de cada lado é o piso do vizinho
 * com a SOMBRA da parede por cima — mais fundo junto à pedra, a esbater para fora.
 * A primeira versão pintava essa sombra opaca, a evitar "prometer piso onde não
 * há"; saiu ao contrário, porque uma mancha escura chapada lê-se como parede e a
 * barra continuava a parecer ter os 32px todos. O `open()` daqui é o que garante
 * que há mesmo piso a mostrar de um dos lados.
 */
export function isVerticalWallRun(col, row) {
    const open = (c, r) => floorAt(c, r) != null || isOutside(c, r);
    if (!isWall(col, row) || open(col, row))
        return false;
    return !open(col, row - 1) && !open(col, row + 1) && (open(col - 1, row) || open(col + 1, row));
}
/**
 * Onde cai um lançamento de `de` para `para` com o alcance dado: o próprio
 * `para` se estiver perto, senão o ponto no caminho até ele, à distância máxima.
 * Serve à bola de neve — no servidor, que não confia no cliente, e no cliente.
 */
export function limitarLancamento(de, para, alcance) {
    const dx = para.x - de.x;
    const dy = para.y - de.y;
    const d = Math.hypot(dx, dy);
    if (d <= alcance || d === 0)
        return { x: para.x, y: para.y };
    return { x: de.x + (dx / d) * alcance, y: de.y + (dy / d) * alcance };
}
/** Um tile é parede? Fora da grelha conta como parede (não há por onde fugir). */
export function isWall(col, row) {
    if (row < 0 || row >= ROWS || col < 0 || col >= COLS)
        return true;
    const line = WALLS[row];
    return line ? line[col] === true : true;
}
/**
 * O ponto em pixels é pisável? Converte para tile e consulta a grelha.
 * Colisão por ponto (o centro do avatar) — chega para a Fase 1.
 */
export function isWalkable(px, py) {
    if (px < 0 || py < 0 || px >= MAP_WIDTH || py >= MAP_HEIGHT)
        return false;
    return !isWall(Math.floor(px / TILE), Math.floor(py / TILE));
}
/**
 * Prende uma posição aos limites do mapa. É o único controlo que o servidor faz:
 * confia no cliente para a colisão fina (não há anti-cheat na Fase 1), mas nunca
 * deixa alguém aparecer fora do chão.
 */
export function clampToSpace(x, y) {
    return {
        x: Math.min(Math.max(x, 0), MAP_WIDTH),
        y: Math.min(Math.max(y, 0), MAP_HEIGHT),
    };
}
// ── Corpo sem mapa: presença de quem não tem escritório 2D ──
//
// Um telemóvel não desenha o escritório — mas tem uma pessoa do outro lado, e
// essa pessoa está algures. Quem está ao computador tem de a ver: entra no
// servidor e aparece na Recepção; entra numa call e aparece na sala dessa call.
// Não é um modo especial do protocolo — é o MESMO `EnterSpace`/`UpdatePosition`
// de sempre, só que a posição vem de uma regra em vez de vir do teclado. Estas
// duas funções são essa regra, e vivem aqui (e não no cliente) porque o mapa é
// que sabe onde é a Recepção e onde é que numa sala se pode estar de pé.
/**
 * A sala cujo nome casa com um canal de VOZ, ou `null` se não houver nenhuma.
 *
 * É a MESMA correspondência que o escritório já corre ao contrário — entrar na
 * sala "TI" liga à call "TI" (`OfficeSpace.onRoomChange`) — escrita uma vez
 * para os dois sentidos não poderem divergir. Comparação por nome e não por id
 * porque é o nome que o servidor semeia nos canais a partir de
 * `OFFICE_VOICE_ROOMS`: o nome É a chave estrangeira entre os dois mundos.
 *
 * Devolver `null` é um resultado legítimo e comum: "Sala Principal" ou
 * "Partilha de Ecrã" são canais de voz que não são sala nenhuma. Quem chama
 * fica na Recepção — inventar-lhes um sítio no mapa era pior do que não ter.
 */
export function roomByVoiceName(name) {
    const wanted = name.trim().toLowerCase();
    if (wanted === "")
        return null;
    return ROOMS.find((r) => r.name !== "" && r.name.toLowerCase() === wanted) ?? null;
}
/**
 * Um sítio onde se pode ESTAR DE PÉ dentro de uma sala, em pixels.
 *
 * O centro geométrico não serve: nas salas com mesa é exactamente onde a mesa
 * está, e um avatar publicado ali aparece em cima do móvel no ecrã de toda a
 * gente. Por isso o centro é só o ponto de partida de uma espiral quadrada que
 * abre até encontrar chão livre — e "livre" aqui é a sério, porque `buildWalls`
 * assa a mobília sólida na grelha de colisão antes de `isWalkable` a ler.
 *
 * Determinista de propósito: a mesma sala dá sempre o mesmo tile, em qualquer
 * cliente e no servidor, sem estado nenhum a sincronizar. Duas pessoas caem no
 * mesmo sítio e ficam sobrepostas — é o preço de não haver estado, e o mundo já
 * desenha avatares sobrepostos quando duas pessoas se encostam a andar.
 */
export function standingSpotIn(roomId) {
    const room = ROOMS.find((r) => r.id === roomId);
    if (!room)
        return null;
    const cx = room.col + Math.floor(room.w / 2);
    const cy = room.row + Math.floor(room.h / 2);
    // O maior lado chega para varrer a sala inteira a partir de qualquer centro.
    const reach = Math.max(room.w, room.h);
    for (let ring = 0; ring <= reach; ring++) {
        for (let dy = -ring; dy <= ring; dy++) {
            for (let dx = -ring; dx <= ring; dx++) {
                // Só a casca do anel — o interior já foi visto na volta anterior.
                if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring)
                    continue;
                const col = cx + dx;
                const row = cy + dy;
                if (!inRect(col, row, room))
                    continue;
                const x = (col + 0.5) * TILE;
                const y = (row + 0.5) * TILE;
                if (isWalkable(x, y))
                    return { x, y };
            }
        }
    }
    return null;
}
// ── Sentar ──────────────────────────────────────────────────
//
// Sentar não é um comando nem uma mensagem nova do protocolo: é GEOMETRIA. Se
// estás parado em cima de um assento, estás sentado. Ponto. A regra corre igual
// em todos os clientes sobre dados que já circulam (a posição de cada um), por
// isso toda a gente vê o mesmo sem o servidor arbitrar nada — exactamente o
// mesmo truque das portas automáticas.
//
// Consequência de desenho: um assento NUNCA pode ser sólido (senão nunca se
// chegava lá) — a invariante está fechada por teste em `space.test.ts`.
/** Mobiliário em que uma pessoa se senta ao pisá-lo. */
const SEAT_KINDS = new Set([
    "officeChair",
    "meetingChair",
    "diningChair",
    "couch",
    "couchSide",
]);
/**
 * Aquilo para onde vale a pena olhar sentado: a mesa a que se está sentado.
 *
 * Ninguém se senta de costas para a mesa de reunião — mas era EXACTAMENTE isso
 * que acontecia enquanto o avatar sentado só sabia a pose virada a sul. A direcção
 * não precisa de ser escrita à mão cadeira a cadeira (seria uma lista a
 * dessincronizar-se do mobiliário à primeira mudança): LÊ-SE da planta. Uma
 * cadeira encostada a uma mesa vira-se para ela, e pronto.
 */
export const FOCUS_KINDS = new Set([
    "deskPC",
    "salesDesk",
    "deskDual",
    "meetingTable",
    "diningTable",
    "receptionDesk",
    "coffeeTable",
    "gameTable",
]);
/**
 * Que móvel-foco ocupa cada tile, para o teste de vizinhança ser O(1).
 *
 * Guarda o MÓVEL e não só a marca do tile porque o alinhamento do lugar (ver
 * `seatAnchor`) precisa de saber onde está o centro da mesa e quantas pessoas
 * ela serve — coisas que um `Set<string>` de tiles não sabe responder.
 */
const FOCUS_AT = (() => {
    const out = new Map();
    for (const prop of PROPS) {
        if (!FOCUS_KINDS.has(prop.kind))
            continue;
        const spec = PROP_SPECS[prop.kind];
        for (let dy = 0; dy < spec.h; dy++) {
            for (let dx = 0; dx < spec.w; dx++)
                out.set(`${prop.col + dx},${prop.row + dy}`, prop);
        }
    }
    return out;
})();
/**
 * Ordem de desempate quando uma cadeira toca em duas mesas: norte, sul, oeste,
 * este. Nunca acontece na planta actual — e se um dia acontecer, é preferível uma
 * escolha ESTÁVEL a uma que dependa da ordem em que os móveis foram escritos.
 */
const FACING_ORDER = [
    ["up", 0, -1],
    ["down", 0, 1],
    ["left", -1, 0],
    ["right", 1, 0],
];
/**
 * Assentos cuja direcção NÃO se lê da planta: o encosto está desenhado num lado
 * fixo, e é a arte que manda. Quem se senta num sofá olha para onde o sofá deixa,
 * mesmo que a mesa de café esteja noutro sítio.
 *
 * É um mapa e não dois `if` porque há mais do que um sofá: o de frente tem o
 * encosto a norte (olha para sul) e o de perfil tem-no a este, encostado à parede
 * da direita (olha para oeste — para dentro da sala). Os testes de "cada cadeira
 * olha mesmo para a mesa que serve" perguntam a ESTE mapa quem está isento, em
 * vez de repetirem os nomes: uma segunda lista desalinhava-se no dia em que
 * entrasse um terceiro sofá, e o sintoma seria um teste a falhar no móvel errado.
 */
export const COUCH_FACING = new Map([
    ["couch", "down"],
    ["couchSide", "left"],
]);
function facingFor(kind, col, row) {
    const encostoFixo = COUCH_FACING.get(kind);
    if (encostoFixo)
        return encostoFixo;
    for (const [dir, dc, dr] of FACING_ORDER) {
        if (FOCUS_AT.has(`${col + dc},${row + dr}`))
            return dir;
    }
    return "down";
}
/**
 * Todos os lugares do escritório, um por tile de pegada — um sofá 3×1 dá TRÊS
 * lugares (cabem três pessoas lado a lado), uma cadeira dá um.
 */
export const SEATS = (() => {
    const seats = [];
    for (const prop of PROPS) {
        if (!SEAT_KINDS.has(prop.kind))
            continue;
        const spec = PROP_SPECS[prop.kind];
        for (let dy = 0; dy < spec.h; dy++) {
            for (let dx = 0; dx < spec.w; dx++) {
                const col = prop.col + dx;
                const row = prop.row + dy;
                seats.push({ col, row, kind: prop.kind, facing: facingFor(prop.kind, col, row) });
            }
        }
    }
    return seats;
})();
const SEAT_AT = new Map(SEATS.map((seat) => [`${seat.col},${seat.row}`, seat]));
/** O ponto em pixels cai num lugar sentado? `null` = chão normal. */
export function seatAt(px, py) {
    if (px < 0 || py < 0 || px >= MAP_WIDTH || py >= MAP_HEIGHT)
        return null;
    return SEAT_AT.get(`${Math.floor(px / TILE)},${Math.floor(py / TILE)}`) ?? null;
}
/** O móvel a que este lugar serve — o que está do lado para onde a pessoa olha. */
function focoDoLugar(seat) {
    for (const [dir, dc, dr] of FACING_ORDER) {
        if (dir !== seat.facing)
            continue;
        return FOCUS_AT.get(`${seat.col + dc},${seat.row + dr}`) ?? null;
    }
    return null;
}
/**
 * Quantos lugares cada móvel-foco serve. É esta contagem que separa uma
 * SECRETÁRIA (uma cadeira, uma pessoa, um posto) de uma MESA DE REUNIÃO (seis
 * cadeiras à volta): a primeira pode alinhar-se com quem lá trabalha, a segunda
 * não pode alinhar-se com ninguém sem desalinhar os outros cinco.
 */
const LUGARES_POR_FOCO = (() => {
    const out = new Map();
    for (const seat of SEATS) {
        const foco = focoDoLugar(seat);
        if (foco)
            out.set(foco, (out.get(foco) ?? 0) + 1);
    }
    return out;
})();
/** Quanto o corpo se chega à frente, na direcção do móvel, em pixels. */
const SEAT_APPROACH = 5;
/**
 * Limite do ajuste lateral. Meio tile menos um pixel: o encaixe TEM de continuar
 * dentro do tile do lugar, senão `seatAt(seatAnchor(s))` deixava de devolver `s`
 * e a pessoa levantava-se sozinha no frame seguinte.
 */
const SEAT_NUDGE_MAX = TILE / 2 - 1;
/**
 * Onde a almofada REALMENTE está, num sofá de encosto fixo.
 *
 * Um sofá não é uma fila de cadeiras: as almofadas não nascem alinhadas com os
 * tiles da pegada, porque o móvel tem braços e encosto a comer espaço. Quem se
 * senta no centro do TILE senta-se, portanto, ao lado da almofada — que é
 * exactamente a queixa *"esta sentando errado no sofá, tem q deixar certo e
 * centralizado"*. Duas contas, uma por eixo, ambas medidas na arte:
 *
 * `passo` — de quanto em quanto se repete a almofada AO COMPRIDO do sofá. No de
 * frente a tela tem 96 px (3 tiles) mas os dois braços comem 10 de cada lado, e
 * as três almofadas ficam com um passo de **25** em vez de 32: o lugar da
 * esquerda precisa de +7, o do meio de 0, o da direita de −7 — todos a convergir
 * para o meio do móvel. Sem isto, os dois lugares das pontas assentam meio corpo
 * em cima do braço. No de perfil as costuras do composto caem em 45/77/109, ou
 * seja de 32 em 32: aí o passo É o tile e não há nada a corrigir.
 *
 * `chega` — quanto a almofada está desviada ATRAVESSADA, no sentido do olhar. No
 * de perfil o tile ocupa x 4..36 da tela de 40 (centro 20) e o assento está em
 * 5..23 (centro 14): **6 px** para oeste, que é para onde esse sofá olha. No de
 * frente é ZERO de propósito, e não por a arte estar centrada — é que o eixo
 * atravessado desse sofá é o VERTICAL, e o vertical já tem dono: `seatDropFor`
 * lê o `CUSHION_TOP` e pousa lá as ancas. Somar aqui era contar duas vezes o
 * mesmo desvio e afundar o corpo no estofo.
 */
const COUCH_CUSHION = new Map([
    ["couch", { passo: 25, chega: 0 }],
    ["couchSide", { passo: TILE, chega: 6 }],
]);
/**
 * A que sofá pertence cada lugar de encosto fixo. É preciso o MÓVEL e não só o
 * lugar porque a correcção depende de qual das almofadas é — a da ponta puxa
 * para um lado, a da outra ponta para o outro.
 */
const SOFA_DO_LUGAR = (() => {
    const out = new Map();
    for (const prop of PROPS) {
        if (!COUCH_FACING.has(prop.kind))
            continue;
        const spec = PROP_SPECS[prop.kind];
        for (let dy = 0; dy < spec.h; dy++) {
            for (let dx = 0; dx < spec.w; dx++)
                out.set(`${prop.col + dx},${prop.row + dy}`, prop);
        }
    }
    return out;
})();
/**
 * O ponto exacto onde o corpo assenta. Base: o centro do tile do lugar — sem
 * isso ficava meio a cavalo no braço do sofá.
 *
 * Por cima disso há um ajuste, e a regra dele sai da planta, não do gosto: um
 * lugar alinha-se com o móvel **só quando esse móvel serve exactamente UMA
 * pessoa**. Uma secretária de 2 tiles com uma cadeira só tem o centro 16 px ao
 * lado do centro da cadeira — e via-se, a pessoa trabalhava na quina esquerda da
 * mesa. Numa mesa de reunião de 6 lugares o mesmo ajuste seria absurdo: puxava
 * as seis pessoas para o meio, umas por cima das outras. Daí a contagem.
 *
 * O ajuste tem dois eixos: aproximar (`SEAT_APPROACH`, no sentido do olhar) e
 * centrar (no eixo transversal), este último preso a `SEAT_NUDGE_MAX` para o
 * corpo nunca sair do tile do próprio lugar.
 */
export function seatAnchor(seat) {
    const x = (seat.col + 0.5) * TILE;
    const y = (seat.row + 0.5) * TILE;
    const preso = (d) => Math.max(-SEAT_NUDGE_MAX, Math.min(SEAT_NUDGE_MAX, d));
    // O sofá decide-se PRIMEIRO e por si: o encosto está desenhado num lado fixo,
    // e onde a almofada está é um facto da arte, não do que houver à frente. Uma
    // mesinha de café ali plantada não tem voto na matéria.
    //
    // E é aqui que morre o *"esta sentando errado no sofá"*: estes lugares nunca
    // chegavam ao ajuste lá de baixo. `focoDoLugar` olha para o tile do lado de
    // onde a pessoa olha à procura de um móvel-foco; um sofá encostado à parede
    // não tem nenhum, portanto devolvia `null` e o `return` seguinte mandava-os
    // embora com o centro do tile cru — ao lado da almofada, todas as vezes.
    const sofa = SOFA_DO_LUGAR.get(`${seat.col},${seat.row}`);
    if (sofa) {
        const spec = PROP_SPECS[sofa.kind];
        const { passo, chega } = COUCH_CUSHION.get(sofa.kind) ?? { passo: TILE, chega: 0 };
        // O comprimento do sofá é sempre PERPENDICULAR ao olhar: quem olha para
        // sul está sentado numa fila que corre de oeste para este.
        const aoComprido = seat.facing === "up" || seat.facing === "down";
        const lugares = aoComprido ? spec.w : spec.h;
        const i = aoComprido ? seat.col - sofa.col : seat.row - sofa.row;
        // Distância do centro DESTE tile ao centro DESTA almofada: as duas séries
        // partem do mesmo meio e afastam-se a ritmos diferentes.
        const desliza = preso((i - (lugares - 1) / 2) * (passo - TILE));
        switch (seat.facing) {
            case "up":
                return { x: x + desliza, y: y - chega };
            case "down":
                return { x: x + desliza, y: y + chega };
            case "left":
                return { x: x - chega, y: y + desliza };
            default:
                return { x: x + chega, y: y + desliza };
        }
    }
    const foco = focoDoLugar(seat);
    if (!foco || LUGARES_POR_FOCO.get(foco) !== 1)
        return { x, y };
    const spec = PROP_SPECS[foco.kind];
    const focoX = (foco.col + spec.w / 2) * TILE;
    const focoY = (foco.row + spec.h / 2) * TILE;
    switch (seat.facing) {
        case "up":
            return { x: x + preso(focoX - x), y: y - SEAT_APPROACH };
        case "down":
            return { x: x + preso(focoX - x), y: y + SEAT_APPROACH };
        case "left":
            return { x: x - SEAT_APPROACH, y: y + preso(focoY - y) };
        default:
            return { x: x + SEAT_APPROACH, y: y + preso(focoY - y) };
    }
}
/**
 * A chave de um lugar — o tile, que é a sua identidade. Um sofá de três tem três
 * chaves, uma por almofada, e é isso que permite duas pessoas no mesmo sofá.
 */
export function seatKey(seat) {
    return `${seat.col},${seat.row}`;
}
/**
 * Quem é o dono de cada lugar ocupado — a regra que fecha o *«se um personagem já
 * estiver sentado, outro pode sentar por cima»*.
 *
 * Sentar continua a ser GEOMETRIA (ver a nota lá em cima): não há comando, nem
 * mensagem nova, nem árbitro no servidor. O que faltava era que a geometria não
 * dizia de QUEM é o lugar quando dois corpos caem no mesmo tile — e como o tile de
 * um assento é deliberadamente atravessável (senão nunca lá se chegava), nada os
 * impedia de se sobreporem.
 *
 * Esta função é a arbitragem, e é PURA de propósito: corre igual em todos os
 * clientes a partir de posições que já circulam, portanto todos chegam ao MESMO
 * dono sem trocar uma única mensagem. É o mesmo truque das portas automáticas.
 *
 * Duas decisões que a seguram:
 *   1. **quem anda não reclama nada** — atravessar um lugar ocupado é legítimo e
 *      não pode roubá-lo a quem lá está sentado;
 *   2. **desempate pelo id MENOR**, comprimento primeiro e depois lexicográfico —
 *      a mesma ordem dos snowflakes, portanto ganha quem entrou primeiro. Qualquer
 *      critério determinístico servia para não haver divergência entre clientes;
 *      este ainda por cima é justo.
 */
export function seatOwners(bodies) {
    const donos = new Map();
    for (const body of bodies) {
        if (body.moving)
            continue;
        const seat = seatAt(body.x, body.y);
        if (!seat)
            continue;
        const chave = seatKey(seat);
        const atual = donos.get(chave);
        if (atual === undefined || menorId(body.id, atual))
            donos.set(chave, body.id);
    }
    return donos;
}
/**
 * `a` vem antes de `b`? Comprimento primeiro, depois lexicográfico — o mesmo
 * critério do `idCompare` da app web, aqui em duas linhas em vez de importado,
 * porque este pacote não depende dela e um id é só texto.
 */
function menorId(a, b) {
    if (a.length !== b.length)
        return a.length < b.length;
    return a < b;
}
/**
 * Este lugar está tomado por OUTRA pessoa?
 *
 * O guarda que se põe no movimento: um lugar ocupado passa a ser sólido para toda
 * a gente menos para quem lá está sentado. Note-se que isto NÃO mexe no
 * `PROP_SPECS.solid` — um assento continua estaticamente atravessável, que é a
 * invariante fechada por teste; o que fica sólido é o INSTANTE em que alguém o
 * ocupa.
 */
export function seatTaken(px, py, owners, selfId) {
    const seat = seatAt(px, py);
    if (!seat)
        return false;
    const dono = owners.get(seatKey(seat));
    return dono !== undefined && dono !== selfId;
}
/**
 * A distância a que cada tile está do mundo livre — e a razão de ela existir.
 *
 * Dar corpo a um assento ocupado é acrescentar paredes ao mapa, e paredes novas
 * fazem sempre a mesma pergunta: dá para prender alguém sem querer? Dá. O
 * escritório tem sete tiles de chão a que só se chega ATRAVESSANDO uma cadeira —
 * o canto entre a secretária e a parede em Vendas, a ponta do sofá na TI, a faixa
 * entre a mesa da Copa e a parede oeste. São cantos mortos onde ninguém vai de
 * propósito; mas quem lá esteja quando outra pessoa se senta na cadeira da
 * entrada fica fechado, sem porta, sem aviso e sem nada que possa fazer.
 *
 * Podia-se emendar a planta das quatro salas. Isto é melhor por duas razões: vale
 * também para os becos que a próxima arrumação de mobília venha a criar sem
 * ninguém dar por isso, e é UMA regra em vez de quatro correcções que se desfazem
 * sozinhas na primeira vez que alguém arraste um sofá.
 *
 * `TERRA_FIRME` é o que se alcança do spawn com TODOS os assentos sólidos — o
 * mundo que sobrevive ao pior caso. Daí conta-se para trás: cada tile guarda
 * quantos passos lhe faltam para lá chegar. Zero é estar lá. É esta a medida que
 * distingue *sair* de um beco (o número desce) de *entrar* nele (sobe ou empata).
 */
const SEAT_ESCAPE = (() => {
    const assento = new Set(SEATS.map((s) => `${s.col},${s.row}`));
    const spawn = [Math.floor(SPAWN.x / TILE), Math.floor(SPAWN.y / TILE)];
    // As escotilhas são arestas de sentido único do grafo do mundo. Sem elas o
    // subsolo inteiro contava como beco, que é o oposto do que ele é.
    const salta = (c, r) => {
        const saidas = [];
        for (const t of TELEPORTS) {
            if (c >= t.zone.col && c < t.zone.col + t.zone.w && r >= t.zone.row && r < t.zone.row + t.zone.h) {
                saidas.push([t.to[0], t.to[1]]);
            }
        }
        return saidas;
    };
    const chegaDe = new Map();
    for (const t of TELEPORTS) {
        const destino = `${t.to[0]},${t.to[1]}`;
        const origens = chegaDe.get(destino) ?? [];
        for (let dy = 0; dy < t.zone.h; dy++) {
            for (let dx = 0; dx < t.zone.w; dx++)
                origens.push([t.zone.col + dx, t.zone.row + dy]);
        }
        chegaDe.set(destino, origens);
    }
    const terraFirme = new Set();
    const pilha = [spawn];
    while (pilha.length) {
        const [c, r] = pilha.pop();
        const chave = `${c},${r}`;
        if (terraFirme.has(chave) || isWall(c, r) || assento.has(chave))
            continue;
        terraFirme.add(chave);
        pilha.push([c + 1, r], [c - 1, r], [c, r + 1], [c, r - 1], ...salta(c, r));
    }
    // Onda para trás a partir de terra firme, agora COM os assentos a contar como
    // chão — um tile entra quando se descobre um passo seu que encurta a saída.
    const passos = new Map();
    let fronteira = [...terraFirme].map((k) => k.split(",").map(Number));
    for (let d = 1; fronteira.length; d++) {
        const seguinte = [];
        for (const [uc, ur] of fronteira) {
            const vizinhos = [
                [uc + 1, ur],
                [uc - 1, ur],
                [uc, ur + 1],
                [uc, ur - 1],
                ...(chegaDe.get(`${uc},${ur}`) ?? []),
            ];
            for (const [tc, tr] of vizinhos) {
                const chave = `${tc},${tr}`;
                if (terraFirme.has(chave) || passos.has(chave) || isWall(tc, tr))
                    continue;
                passos.set(chave, d);
                seguinte.push([tc, tr]);
            }
        }
        fronteira = seguinte;
    }
    return passos;
})();
/** Quantos passos faltam a este ponto para chegar ao mundo que nunca fecha. */
function escapeSteps(px, py) {
    if (px < 0 || py < 0 || px >= MAP_WIDTH || py >= MAP_HEIGHT)
        return 0;
    return SEAT_ESCAPE.get(`${Math.floor(px / TILE)},${Math.floor(py / TILE)}`) ?? 0;
}
/** Este ponto é um beco — chão a que só se chega por cima de um assento? */
export function inSeatPocket(px, py) {
    return escapeSteps(px, py) > 0 && !seatAt(px, py);
}
/**
 * Um assento ocupado barra o caminho — **excepto a quem está a sair de um beco**.
 *
 * É esta a regra do movimento, e a excepção é o que a torna segura: um lugar
 * tomado é sólido para toda a gente menos para o dono e para quem, sem ela,
 * ficaria fechado num canto morto. Ninguém se senta em cima de ninguém e ninguém
 * fica preso — as duas coisas ao mesmo tempo, sem tocar na planta de uma sala.
 *
 * A excepção é estreita de propósito: o passo tem de **encurtar** a distância à
 * terra firme. Atravessa-se uma cadeira ocupada a caminho da saída, nunca a
 * caminho de mais fundo — e por isso ela vale em cadeia (um beco atrás de duas
 * cadeiras sai à mesma) sem nunca abrir a porta a sentar-se por cima de alguém:
 * quem já está em terra firme tem distância zero e não tem nada que encurtar.
 *
 * Repare-se que isto NÃO mexe no `PROP_SPECS.solid`: estaticamente um assento
 * continua a ser chão, que é a invariante fechada por teste e a razão de se lá
 * conseguir chegar. O que ganha corpo é só o INSTANTE em que alguém o ocupa.
 */
export function seatBlocks(from, to, owners, selfId) {
    if (!seatTaken(to.x, to.y, owners, selfId))
        return false;
    const daqui = escapeSteps(from.x, from.y);
    return !(daqui > 0 && daqui > escapeSteps(to.x, to.y));
}
// ── Trancar portas ──────────────────────────────────────────
//
// Trancar uma sala é um gesto FÍSICO no mundo, não uma definição do servidor:
// estás lá dentro, viras-te para a porta e trancas — como numa reunião. Por isso
// o estado é EFÉMERO (vive no Redis com os ocupantes, não na base de dados) e as
// regras são as de uma porta de verdade:
//   0. nem toda a porta tem ferrolho. As salas comuns — Sala de Descanso,
//      Recepção, Copa — são marcadas `noBolt` na sua linha de `ROOMS`: entra-se,
//      bate-se-lhes, mas não se trancam. Fechar a entrada da casa ou a
//      máquina do café não protegia trabalho nenhum de ninguém;
//   1. só tranca quem está DENTRO da sala;
//   2. quem está dentro sai sempre — a tranca prende os outros FORA, nunca
//      aprisiona os de dentro;
//   3. a tranca cai sozinha quando o último ocupante sai. Uma sala vazia e
//      trancada seria uma sala morta que ninguém mais conseguiria abrir — é por
//      isto que não é preciso nenhuma "chave-mestra" de administrador.
// Isto é ORTOGONAL às permissões: a permissão `CONNECT` decide quem PODE usar a
// sala (política, permanente); a tranca decide quem entra AGORA (momento).
/**
 * Esta sala tem ferrolho? (regra 0)
 *
 * Total de propósito — responde `false` a um id que não existe e a um espaço sem
 * porta, para poder ser o guarda de qualquer caminho sem um `find` a repetir-se
 * antes dela.
 */
export function canLockRoom(roomId) {
    const room = ROOMS.find((r) => r.id === roomId);
    return room !== undefined && room.name !== "" && room.noBolt !== true;
}
/** A que sala pertence um tile de porta — a divisão que ela fecha. */
function roomIdAdjacentTo(col, row) {
    for (const [dc, dr] of [
        [0, -1],
        [0, 1],
        [-1, 0],
        [1, 0],
    ]) {
        for (const r of ROOMS) {
            if (r.name !== "" && inRect(col + dc, row + dr, r))
                return r.id;
        }
    }
    return null;
}
/**
 * Portas agrupadas pela sala que fecham. Derivado de `DOORS` — a fonte única
 * continua a ser a lista de vãos; isto é só o índice inverso de que o cliente
 * precisa para desenhar o ferrolho no sítio certo.
 */
export const DOORS_BY_ROOM = (() => {
    const out = new Map();
    for (const [col, row] of DOORS) {
        const id = roomIdAdjacentTo(col, row);
        if (!id)
            continue;
        const list = out.get(id);
        if (list)
            list.push([col, row]);
        else
            out.set(id, [[col, row]]);
    }
    return out;
})();
/** Os vãos que fecham esta sala (vazio se a sala não tiver porta própria). */
export function doorTilesOf(roomId) {
    return DOORS_BY_ROOM.get(roomId) ?? [];
}
/** Meia-largura da faixa reservada, em tiles (6 tiles = 192px > o nome mais longo). */
const LABEL_HALF_COLS = 3;
export const ROOM_LABELS = (() => {
    const out = [];
    for (const room of ROOMS) {
        if (room.name === "")
            continue;
        const doors = doorTilesOf(room.id);
        if (doors.length === 0)
            continue;
        // Alinhado com o vão: o nome fica mesmo por cima de quem entra.
        const cx = doors.reduce((sum, [col]) => sum + col, 0) / doors.length + 0.5;
        const fromNorth = doors[0][1] < room.row;
        const row = fromNorth ? room.row : room.row + room.h - 1;
        const first = Math.round(cx) - LABEL_HALF_COLS;
        const band = [];
        for (let dr = 0; dr < 2; dr++) {
            for (let i = 0; i < LABEL_HALF_COLS * 2; i++)
                band.push([first + i, row + dr]);
        }
        out.push({ roomId: room.id, text: room.name, x: cx * TILE, y: (row + 0.5) * TILE, band });
    }
    return out;
})();
/**
 * Estás suficientemente perto de uma porta da tua sala para lhe mexer no
 * ferrolho? Mede-se ao CENTRO do vão em pixels — é o mesmo critério com que as
 * portas exteriores abrem por proximidade, para o mundo ser coerente.
 */
export const DOOR_REACH = TILE * 2.2;
export function nearDoorOf(px, py, roomId) {
    for (const [col, row] of doorTilesOf(roomId)) {
        const cx = col * TILE + TILE / 2;
        const cy = row * TILE + TILE / 2;
        if (Math.hypot(px - cx, py - cy) <= DOOR_REACH)
            return true;
    }
    return false;
}
/** Tile de vão → sala que ele fecha. Índice do `roomOrDoorway`, calculado uma vez. */
const DOORWAY_OWNER = (() => {
    const out = new Map();
    for (const [roomId, tiles] of DOORS_BY_ROOM) {
        for (const [col, row] of tiles)
            out.set(`${col},${row}`, roomId);
    }
    return out;
})();
/**
 * A sala a que este ponto pertence CONTANDO a soleira — a diferença silenciosa
 * que faz a tranca funcionar.
 *
 * `roomAt` devolve null em cima de um vão (os vãos ficam na linha de parede ENTRE
 * as divisões, fora do rectângulo de qualquer sala). Tomado à letra, isso deixava
 * um estranho ficar PARADO DENTRO da porta de uma sala trancada: não entrava na
 * sala, mas plantava-se no vão, e a porta tinha de abrir para ele. Uma porta
 * trancada fecha o vão, não a linha logo a seguir ao vão.
 *
 * `roomAt` fica exactamente como está — quem decide a call por cargo é ela, e
 * pisar a soleira não é entrar na reunião.
 */
export function roomOrDoorway(px, py) {
    const here = roomAt(px, py);
    if (here)
        return here.id;
    const col = Math.floor(px / TILE);
    const row = Math.floor(py / TILE);
    return DOORWAY_OWNER.get(`${col},${row}`) ?? null;
}
/**
 * A sala cuja porta consigo trancar de onde estou — ou null.
 *
 * Cuidado com a armadilha que isto resolve: os vãos ficam nas linhas de parede
 * ENTRE as salas, logo `roomAt` devolve NULL em cima do vão. Exigir "estar dentro
 * da sala" fazia o aviso desaparecer exactamente no passo em que se chega à porta
 * — o gesto morria no último centímetro. Aqui a soleira conta como estar à porta.
 *
 * O cliente usa-a para mostrar o aviso e o gateway usa-a para aceitar o pedido —
 * a mesma função, para que nunca haja um aviso que o servidor recusa.
 *
 * Repara no `roomOrDoorway` a abrir: o ferrolho é do lado DE DENTRO (regra 1).
 * Estar no corredor encostado à porta não chega — senão qualquer um trancava por
 * fora uma sala alheia, com gente lá dentro.
 *
 * E é aqui — num só sítio — que a regra 0 se aplica: uma sala `noBolt` devolve
 * null, portanto o aviso apaga-se no cliente E o pedido cai no gateway pela mesma
 * linha. Isto tapa também o DESTRANCAR dessas salas, e é seguro: pela regra 3 a
 * tranca cai sozinha quando a sala fica vazia, logo uma sala `noBolt` só pode
 * ficar trancada de um mundo anterior à regra 0 e com gente lá dentro — e essa
 * gente, ao sair, desfaz o nó.
 */
export function lockableRoomAt(px, py) {
    const here = roomOrDoorway(px, py);
    return here !== null && canLockRoom(here) && nearDoorOf(px, py, here) ? here : null;
}
/**
 * A ÚNICA regra de passagem numa porta trancada — partilhada de propósito: o
 * cliente usa-a para o avatar bater na porta a 60fps, e o gateway usa-a EXACTAMENTE
 * na mesma forma para recusar a posição. Sem a metade do servidor, a tranca era
 * decoração: bastava um cliente modificado para a atravessar.
 *
 * Deixa passar quando o destino não é sala trancada, e quando já se ESTAVA lá
 * dentro (regra 2: sair é sempre permitido, e quem trancou não fica preso).
 *
 * Mede com `roomOrDoorway`, não com `roomAt`: a soleira é da sala, senão a tranca
 * parava a gente um passo DEPOIS da porta e um estranho podia acampar no vão.
 */
export function canEnter(from, to, isLocked) {
    const target = roomOrDoorway(to.x, to.y);
    if (!target || !isLocked(target))
        return true;
    return roomOrDoorway(from.x, from.y) === target;
}
/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  Encontros de corredor, caminhos e alcance
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Tudo o que se segue é geometria PURA e determinista: dadas as mesmas posições,
 * qualquer cliente e o servidor chegam à mesma resposta. É essa propriedade que
 * permite ao huddle de corredor não existir em lado nenhum como estado — não há
 * "criar huddle", nem linha em base de dados, nem dono: há uma regra que toda a
 * gente calcula por cima de posições que já circulavam na rede. É o mesmo truque
 * das cadeiras (pisar o assento = sentar) e das portas automáticas.
 */
/** Perto o suficiente para se falar de pé no corredor. */
export const HUDDLE_RADIUS = TILE * 2.8;
/** Um encontro de corredor com meia empresa não é um encontro de corredor. */
export const HUDDLE_MAX_MEMBERS = 8;
/** Alcance de leitura de um balão de texto (só afecta o desenho, não a rede). */
export const SAY_RANGE = TILE * 10;
/** Distância a que se chega a um móvel para lhe mexer com o E. */
export const PROP_REACH = TILE * 1.8;
/**
 * Ordem estável entre ids. Não é a ordem numérica verdadeira de um snowflake
 * (isso pedia BigInt); é só uma ordem TOTAL e igual para toda a gente, que é o
 * que a âncora precisa. Compara primeiro por comprimento para que "1000" venha
 * depois de "999" — a ordem lexicográfica pura trocava-os.
 */
function cmpId(a, b) {
    return a.length - b.length || (a < b ? -1 : a > b ? 1 : 0);
}
/**
 * Como se chama o sítio onde esta pessoa está — a resposta que o cabeçalho dá.
 *
 * O cabeçalho perguntava só `roomAt`, e `roomAt` responde a UMA coisa: em que
 * SALA COM NOME estás. Tudo o resto vinha null e era desenhado como "Corredor" —
 * o que dizia "Corredor" a quem estivesse no jardim, no parque das casas, no
 * estacionamento ou no bunker. Um rótulo errado é pior do que rótulo nenhum:
 * quem está lá fora a ver "Corredor" duvida do resto do que a barra diz.
 *
 * As três perguntas seguintes já existiam, cada uma respondida por quem sabe:
 * `isUnderground` pela linha da superfície, `isOutside` pelo rectângulo do
 * prédio, e o que sobra é mesmo o corredor. Isto só as põe por ordem — não
 * inventa geometria nenhuma, e é por isso que não pode divergir do mapa.
 *
 * Vive aqui e não no cliente porque os nomes das salas ("Copa", "Vendas") já
 * vivem aqui: ter METADE dos nomes de sítios num módulo e a outra metade dentro
 * de um componente era garantir que um dia diriam coisas diferentes.
 */
export function placeName(px, py) {
    const room = roomAt(px, py);
    if (room)
        return room.name;
    const col = Math.floor(px / TILE);
    const row = Math.floor(py / TILE);
    // A ORDEM destas três é significativa, e cada uma explica a seguinte.
    //
    // O subsolo primeiro porque está para lá da cerca no eixo do Y (`row >= 52` é
    // sempre `>= PARK_ROW`) e cairia no pátio se viesse depois. Não é jardim nem
    // pátio — é debaixo de tudo.
    //
    // O pátio antes do exterior porque TAMBÉM é exterior: `isOutside` é verdadeiro
    // lá dentro, e a pergunta mais específica tem de ser feita primeiro ou nunca
    // chega a ser feita.
    if (isUnderground(row))
        return "Subsolo";
    if (isUpstairs(row))
        return "1.º andar";
    if (row >= PARK_ROW)
        return "Pátio de Mobile Homes";
    if (isOutside(col, row))
        return "Lá fora";
    return "Corredor";
}
/**
 * Onde é que um encontro de corredor pode acontecer: fora de qualquer sala com
 * nome. Corredor, recinto, jardim, estacionamento — sim; dentro da Sala de
 * Reunião — não.
 *
 * Esta linha vale mais do que parece: é ela que torna IMPOSSÍVEL estar numa call
 * de sala e num huddle ao mesmo tempo, sem uma única linha de exclusão mútua. O
 * cliente só tem uma ligação de voz por separador (ver `voice.ts`), e as duas
 * fontes que a disputam vivem em zonas geograficamente disjuntas.
 */
export function huddleZone(px, py) {
    const here = roomOrDoorway(px, py);
    if (here === null)
        return true;
    const room = ROOMS.find((r) => r.id === here);
    return !room || room.name === "";
}
/**
 * Agrupa quem está perto de quem, em zona de corredor: as componentes ligadas de
 * um grafo em que há aresta entre duas pessoas a menos de `HUDDLE_RADIUS`.
 *
 * A ligação é transitiva de propósito (A—B—C é UM grupo, mesmo com A longe de C):
 * é assim que uma roda de conversa funciona na vida real, e a alternativa (só
 * pares) partia um grupo de três em três calls que não se ouviam umas às outras.
 *
 * A âncora é o MENOR id do grupo — determinista, sem estado e sem negociação.
 * Custo honesto: se chegar alguém com id menor, a âncora muda e o grupo reconecta
 * (um blip de audio). Preferi isso a uma regra pegajosa que dependesse do que
 * cada cliente tinha visto ANTES — dois clientes com históricos diferentes
 * escolheriam âncoras diferentes, e o resultado disso é silêncio permanente.
 *
 * `inZone` é o que separa o corredor da sala — e continua a ser um PARÂMETRO com
 * um só mundo em pé: o grafo (quem está perto de quem) não sabe nada de plantas,
 * e é a única peça que não precisa de mudar se um dia houver outra.
 */
export function huddleGroups(bodies, inZone = huddleZone) {
    const eligible = bodies.filter((b) => inZone(b.x, b.y)).sort((a, b) => cmpId(a.userId, b.userId));
    const n = eligible.length;
    if (n < 2)
        return [];
    const parent = eligible.map((_, i) => i);
    const find = (i) => {
        let root = i;
        while (parent[root] !== root)
            root = parent[root];
        let cur = i;
        while (parent[cur] !== root) {
            const next = parent[cur];
            parent[cur] = root;
            cur = next;
        }
        return root;
    };
    const r2 = HUDDLE_RADIUS * HUDDLE_RADIUS;
    for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
            const a = eligible[i];
            const b = eligible[j];
            const dx = a.x - b.x;
            const dy = a.y - b.y;
            if (dx * dx + dy * dy <= r2) {
                const ra = find(i);
                const rb = find(j);
                if (ra !== rb)
                    parent[ra] = rb;
            }
        }
    }
    const buckets = new Map();
    for (let i = 0; i < n; i++) {
        const root = find(i);
        const id = eligible[i].userId;
        const list = buckets.get(root);
        if (list)
            list.push(id);
        else
            buckets.set(root, [id]);
    }
    const out = [];
    for (const memberIds of buckets.values()) {
        if (memberIds.length < 2 || memberIds.length > HUDDLE_MAX_MEMBERS)
            continue;
        memberIds.sort(cmpId);
        out.push({ id: memberIds[0], memberIds });
    }
    return out.sort((a, b) => cmpId(a.id, b.id));
}
/** O grupo de corredor a que este utilizador pertence agora — ou null. */
export function huddleOf(groups, userId) {
    return groups.find((g) => g.memberIds.includes(userId)) ?? null;
}
/**
 * A sala TRANCADA a cuja porta estou, vindo de fora — a que posso bater.
 *
 * O simétrico exacto de `lockableRoomAt`: o ferrolho é de dentro, a batida é de
 * fora. Quem está lá dentro nunca vê o aviso de bater (bater à própria porta não
 * faz sentido), e uma porta destrancada também não o mostra — empurra-se e entra.
 */
export function knockableRoomAt(px, py, isLocked) {
    const here = roomOrDoorway(px, py);
    for (const room of ROOMS) {
        if (!room.name || room.id === here)
            continue;
        if (!isLocked(room.id))
            continue;
        if (nearDoorOf(px, py, room.id))
            return room.id;
    }
    return null;
}
const PROP_ACTIONS = {
    whiteboard: "whiteboard",
    // Os computadores das secretárias também não: o aviso "Abrir o canal da
    // equipa" não servia de nada, o canal abre-se no botão Discord.
    // A TV da Sala de Descanso NÃO está aqui de propósito. Carregar nela juntava
    // quem estivesse em qualquer ponto do escritório à call onde alguém partilhava
    // o ecrã — ver a apresentação sem entrar na sala. Uma apresentação vê-se
    // entrando na sala onde ela acontece; a TV fica só como mobília e letreiro.
    gameTable: "game",
    arcade: "rush",
    // O relógio de pé abre a tela de Bater Ponto do painel admin.
    grandfatherClock: "clock",
};
/**
 * O móvel interactivo mais próximo ao alcance da mão, ou null.
 *
 * Mede a distância à PEGADA do móvel e não ao seu centro: um quadro branco tem
 * quatro tiles de largura, e ter de o abrir exactamente a meio seria um puzzle,
 * não uma interacção.
 */
export function interactivePropAt(px, py) {
    let best = null;
    let bestDist = PROP_REACH;
    for (const prop of PROPS) {
        const action = PROP_ACTIONS[prop.kind];
        if (!action)
            continue;
        const spec = PROP_SPECS[prop.kind];
        const x0 = prop.col * TILE;
        const y0 = prop.row * TILE;
        const x1 = x0 + spec.w * TILE;
        const y1 = y0 + spec.h * TILE;
        const dist = Math.hypot(Math.max(x0 - px, 0, px - x1), Math.max(y0 - py, 0, py - y1));
        if (dist > bestDist)
            continue;
        bestDist = dist;
        best = {
            kind: prop.kind,
            action,
            col: prop.col,
            row: prop.row,
            roomId: roomAt((x0 + x1) / 2, (y0 + y1) / 2)?.id ?? null,
        };
    }
    return best;
}
/** Centro do tile indicado. */
function tileCentre(col, row) {
    return { x: (col + 0.5) * TILE, y: (row + 0.5) * TILE };
}
/** Heap binária mínima — o suficiente para o A* não degradar para O(n²). */
class MinHeap {
    nodes = [];
    keys = [];
    get size() {
        return this.nodes.length;
    }
    swap(i, j) {
        const n = this.nodes[i];
        const k = this.keys[i];
        this.nodes[i] = this.nodes[j];
        this.keys[i] = this.keys[j];
        this.nodes[j] = n;
        this.keys[j] = k;
    }
    push(node, key) {
        this.nodes.push(node);
        this.keys.push(key);
        let i = this.nodes.length - 1;
        while (i > 0) {
            const parent = (i - 1) >> 1;
            if (this.keys[parent] <= this.keys[i])
                break;
            this.swap(parent, i);
            i = parent;
        }
    }
    pop() {
        if (this.nodes.length === 0)
            return undefined;
        const top = this.nodes[0];
        const lastNode = this.nodes.pop();
        const lastKey = this.keys.pop();
        if (this.nodes.length > 0) {
            this.nodes[0] = lastNode;
            this.keys[0] = lastKey;
            let i = 0;
            for (;;) {
                const l = i * 2 + 1;
                const r = l + 1;
                let small = i;
                if (l < this.nodes.length && this.keys[l] < this.keys[small])
                    small = l;
                if (r < this.nodes.length && this.keys[r] < this.keys[small])
                    small = r;
                if (small === i)
                    break;
                this.swap(small, i);
                i = small;
            }
        }
        return top;
    }
}
const PATH_NEIGHBOURS = [
    [0, -1],
    [0, 1],
    [-1, 0],
    [1, 0],
];
/**
 * Caminho a pé de um ponto ao outro, em centros de tile.
 *
 * A* a 4 vizinhos — nada de diagonais, de propósito: o movimento manual também
 * colide eixo a eixo, e uma diagonal "por entre" dois cantos passaria no plano e
 * encravava o avatar na prática.
 *
 * As portas trancadas são respeitadas pela MESMA `canEnter` que trava o passo
 * manual, para nunca haver um caminho automático que a tranca depois recusa. Sem
 * `isLocked`, tudo conta como aberto.
 *
 * Devolve só os pontos de INFLEXÃO: um corredor a direito é um waypoint, não
 * trinta. Devolve null se não houver caminho, e um array vazio se já se está lá.
 */
export function findPath(from, to, isLocked = () => false) {
    const sc = Math.floor(from.x / TILE);
    const sr = Math.floor(from.y / TILE);
    const gc = Math.floor(to.x / TILE);
    const gr = Math.floor(to.y / TILE);
    if (gc < 0 || gr < 0 || gc >= COLS || gr >= ROWS)
        return null;
    if (sc < 0 || sr < 0 || sc >= COLS || sr >= ROWS)
        return null;
    const goalPt = tileCentre(gc, gr);
    if (!isWalkable(goalPt.x, goalPt.y))
        return null;
    if (sc === gc && sr === gr)
        return [];
    const total = COLS * ROWS;
    const idx = (c, r) => r * COLS + c;
    const gScore = new Float64Array(total).fill(Number.POSITIVE_INFINITY);
    const cameFrom = new Int32Array(total).fill(-1);
    const closed = new Uint8Array(total);
    const start = idx(sc, sr);
    const goal = idx(gc, gr);
    // A distância conta-se com o 1.º andar DOBRADO sobre o rés-do-chão: o mesmo
    // tile de cima e de baixo está a distância zero. Sem isto a heurística somava
    // 66 filas a qualquer destino lá em cima e deixava de ser um limite inferior.
    const dobra = (r) => (isUpstairs(r) ? r - UPSTAIRS_OFFSET_ROWS : r);
    const h = (c, r) => Math.abs(c - gc) + Math.abs(dobra(r) - dobra(gr));
    gScore[start] = 0;
    const open = new MinHeap();
    open.push(start, h(sc, sr));
    while (open.size > 0) {
        const current = open.pop();
        if (current === goal)
            break;
        if (closed[current])
            continue;
        closed[current] = 1;
        const cc = current % COLS;
        const cr = (current - cc) / COLS;
        const here = tileCentre(cc, cr);
        const visitar = (nc, nr) => {
            const n = idx(nc, nr);
            if (closed[n])
                return;
            const tentative = gScore[current] + 1;
            if (tentative >= gScore[n])
                return;
            gScore[n] = tentative;
            cameFrom[n] = current;
            open.push(n, tentative + h(nc, nr));
        };
        for (const [dc, dr] of PATH_NEIGHBOURS) {
            const nc = cc + dc;
            const nr = cr + dr;
            if (nc < 0 || nr < 0 || nc >= COLS || nr >= ROWS)
                continue;
            const there = tileCentre(nc, nr);
            if (!isWalkable(there.x, there.y))
                continue;
            if (!canEnter(here, there, isLocked))
                continue;
            visitar(nc, nr);
        }
        // A escada é uma aresta a sério, ao contrário da escotilha: o cliente troca
        // de andar sem mexer o boneco no ecrã, e salta sozinho os pontos do caminho
        // que ficaram no outro andar. Não há deslize nenhum a atravessar paredes.
        const escada = destinoDaEscada(cc, cr);
        if (escada)
            visitar(escada[0], escada[1]);
    }
    if (cameFrom[goal] === -1)
        return null;
    const tiles = [];
    for (let cur = goal; cur !== start && cur !== -1; cur = cameFrom[cur]) {
        const c = cur % COLS;
        tiles.push({ c, r: (cur - c) / COLS });
    }
    tiles.reverse();
    // Só os cantos: guardar cada tile de um troço a direito enchia o caminho de
    // paragens inúteis e fazia o passo parecer entrecortado.
    const out = [];
    for (let i = 0; i < tiles.length; i++) {
        const prev = tiles[i - 1] ?? { c: sc, r: sr };
        const cur = tiles[i];
        const next = tiles[i + 1];
        if (!next || next.c - cur.c !== cur.c - prev.c || next.r - cur.r !== cur.r - prev.r) {
            out.push(tileCentre(cur.c, cur.r));
        }
    }
    return out;
}
/**
 * Caminho até ficar AO LADO de alguém, nunca por cima.
 *
 * Tenta os quatro tiles vizinhos por ordem de proximidade a quem anda, e fica
 * pelo primeiro a que consiga mesmo chegar — um vizinho livre no mapa pode estar
 * do lado errado de uma porta trancada, e nesse caso é o seguinte que vale.
 */
export function pathToPerson(from, target, isLocked = () => false) {
    const tc = Math.floor(target.x / TILE);
    const tr = Math.floor(target.y / TILE);
    const candidates = [tileCentre(tc, tr + 1), tileCentre(tc, tr - 1), tileCentre(tc - 1, tr), tileCentre(tc + 1, tr)]
        .filter((p) => isWalkable(p.x, p.y))
        .sort((a, b) => Math.hypot(a.x - from.x, a.y - from.y) - Math.hypot(b.x - from.x, b.y - from.y));
    for (const candidate of candidates) {
        const path = findPath(from, candidate, isLocked);
        if (path)
            return path;
    }
    // Sem vizinho alcançável, vale ir ao próprio tile: melhor sobrepor do que não ir.
    return findPath(from, target, isLocked);
}
//# sourceMappingURL=space.js.map